import SafeMJ from "../../utils/SafeMJ";

import qr_anatomy from "../assets/qr_anatomy_v2.es.png";
import qr_zigzag from "../assets/qr_v2_zigzag.es.png";

function Proof({ children }) {
  return (
    <div className="border-l-4 border-gray-300 dark:border-gray-700 pl-4 mb-3">
      {children}
    </div>
  );
}

export default function ReedSolomon() {
  // Matrices reales 25x25 del QR de https://jaimegimillo.me, versión 2, nivel M,
  // con la máscara 6. Ambas escanean correctamente a la misma URL: la segunda
  // tiene 8 bytes ENTEROS (todos sus bits) puestos a negro —los números 15, 16,
  // 21, 22, 27, 28, 33 y 34—, elegidos para que, al estar en columnas vecinas
  // a la misma altura, formen una mancha compacta en el centro (filas 8-17,
  // columnas 9-16), justo el máximo t=8 corregible.
  const QR_ORIG = `1111111010001010001111111
1000001010000100101000001
1011101010111101101011101
1011101001001101101011101
1011101010100110101011101
1000001000000010001000001
1111111010101010101111111
0000000001111110100000000
1001111111010010110010111
0101110110011111000111110
1100101011001101111001001
0110110101010010101011111
1001101100101010101100001
1011100111000011100010010
1100111110111011000011111
1001110110110011011101101
1011101110000101111110110
0000000010111100100010110
1111111011010100101010001
1000001010001101100010010
1011101010001011111110011
1011101010000011111000011
1011101000101000010011111
1000001001000010000110111
1111111010101110101001001`.split("\n");
  const QR_DMG = `1111111010001010001111111
1000001010000100101000001
1011101010111101101011101
1011101001001101101011101
1011101010100110101011101
1000001000000010001000001
1111111010101010101111111
0000000001111110100000000
1001111111011010110010111
0101110111011111100111110
1100101011111111111001001
0110110101111111101011111
1001101101111111101100001
1011100111111111100010010
1100111111111111100011111
1001110111111111111101101
1011101111110111111110110
0000000010111110100010110
1111111011010100101010001
1000001010001101100010010
1011101010001011111110011
1011101010000011111000011
1011101000101000010011111
1000001001000010000110111
1111111010101110101001001`.split("\n");
  // 1 = este módulo concreto cambió de valor respecto al original por culpa
  // de la mancha (se pinta en rojo, sea cual sea su color final).
  const QR_DIFF = `0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000001000000000000
0000000001000000100000000
0000000000110010000000000
0000000000101101000000000
0000000001010101000000000
0000000000111100000000000
0000000001000100100000000
0000000001001100100000000
0000000001110010000000000
0000000000000010000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000`.split("\n");
    // 9 bytes manchados: 15, 16, 21, 22, 27, 28, 32, 33, 34 (72 bits).
  // Sigue sin decodificar (9 > t=8).
  const QR_DMG_9 = `1111111010001010001111111
1000001010000100101000001
1011101010111101101011101
1011101001001101101011101
1011101011100110101011101
1000001001100010001000001
1111111010101010101111111
0000000001111110100000000
1001111111111010110010111
0101110111111111100111110
1100101011111111111001001
0110110101111111101011111
1001101101111111101100001
1011100111111111100010010
1100111111111111100011111
1001110111111111111101101
1011101111110111111110110
0000000010111110100010110
1111111011010100101010001
1000001010001101100010010
1011101010001011111110011
1011101010000011111000011
1011101000101000010011111
1000001001000010000110111
1111111010101110101001001`.split("\n");
  const QR_DIFF_9 = `0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000001000000000000000
0000000001100000000000000
0000000000000000000000000
0000000001100000000000000
0000000001101000100000000
0000000001111101100000000
0000000001111111100000000
0000000001111111100000000
0000000001111111100000000
0000000001111111100000000
0000000001111111100000000
0000000001111111100000000
0000000001110111000000000
0000000000100010000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000
0000000000000000000000000`.split("\n");

  const renderQR = (rows, caption, label, diffRows = null) => {
    const N = rows.length;
    const cell = 8;
    const quiet = 4;
    const size = (N + 2 * quiet) * cell;
    const rects = [];
    rows.forEach((row, r) => {
      [...row].forEach((ch, c) => {
        const isDiff = diffRows && diffRows[r][c] === "1";
        if (ch === "1" || isDiff) {
          rects.push(
            <rect
              key={`${r}-${c}`}
              x={(c + quiet) * cell}
              y={(r + quiet) * cell}
              width={cell}
              height={cell}
              fill={isDiff ? "#000" : "#000"}
            />
          );
        }
      });
    });
    return (
      <figure className="flex flex-col items-center m-0">
        <svg viewBox={`0 0 ${size} ${size}`} className="w-60 h-60 sm:w-64 sm:h-64 rounded-md shadow" shapeRendering="crispEdges" role="img" aria-label={label}>
          <rect x="0" y="0" width={size} height={size} fill="#fff" />
          {rects}
        </svg>
        <figcaption className="mt-3 max-w-[15rem] sm:max-w-[16rem] text-base text-gray-600 dark:text-gray-400 text-center">{caption}</figcaption>
      </figure>
    );
  };

  return (
    <>
    <p className="mb-3">
      ¿A quién de pequeño no se le ha rayado un poco un videojuego de la PlayStation? Que si tu hermano pequeño,
      que si dichoso gato, que si bueno, lo dejo un momentito fuera de la funda...
      Y sin embargo, cualquier chaval que haya sido de consolas podrá asegurar que ese juego, con un poco de suerte, podía seguir funcionando.
      Eso sí, te garantizo que no era porque le dieras con un trapito por la cara reflectante del disco, ni por el truquito de la saliva que te dijo
      tu primo. El milagro se lo debes, en realidad, a dos tipos: Irving S. Reed y Gustave Solomon, y al método de corrección de errores que desarrollaron en los años 60.
    </p>
    <p className="mb-3">
      Hoy nos centraremos, por tanto, en un caso particular de la <b>teoría de códigos</b>, que como ya sabes estudia métodos para transmitir información de manera eficiente y resiliente, es decir,
      de forma que el receptor pueda recuperar el mensaje original incluso si se han producido errores o alteraciones durante el camino. Fíjate que quien dice enviar y recibir, dice escribir y leer,
      o grabar y reproducir.
    </p>
    <p className="mb-3">
      Y si bien el ejemplo de antes ha sido un poco tonto, en realidad los códigos Reed-Solomon han tenido aplicaciones muy serias desde que se crearon. Tan solo diré
      que hasta finales del siglo XX, eran el método por excelencia en las comunicaciones espaciales de la NASA, así que poca broma.
    </p>
    <p className="mb-3">
      Hoy en día, existen métodos de codificación más modernos y sofisticados, evidentemente, pero Reed-Solomon sigue siendo un estándar muy extendido por su sencillez y eficacia cuando no se maneja un gran volumen de datos.
      Posiblemente, su aplicación actual más conocida sean los códigos QR. Con una pequeña mancha, una esquina cortada o poca iluminación, tu móvil puede seguir escaneándolos perfectamente, y eso es gracias a Reed-Solomon, y,
      si nos ponemos románticos, a los cuerpos finitos y la teoría de Galois. Pero no nos adelantemos, y vayamos paso a paso.
    </p>
    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">1. Un breve repaso de cuerpos finitos</h2>
    <p className="mb-3">
      Toda la maquinaria de Reed-Solomon vive sobre un <em>cuerpo finito</em>, así que permíteme dedicar unos párrafos a recordar este concepto. Si no tienes ni idea de álgebra abstracta, lo primero: lo siento. Yo al menos la recuerdo
      como una de las asignaturas más duras de la carrera. Te invito a que investigues un poco por tu cuenta antes de seguir leyendo o, si no, a quedarte simplemente con las ideas más importantes de esta sección.
    </p>
    <p className="mb-3">
      Dicho esto, empecemos por lo más básico. Ya sabes que un <b>cuerpo</b> es una terna <SafeMJ>{"\\((K,+,\\cdot)\\)"}</SafeMJ> donde:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"><SafeMJ>{"\\((K,+)\\)"}</SafeMJ> es un grupo abeliano con neutro <SafeMJ after=".">{"\\(0\\)"}</SafeMJ></li>
        <li className="mb-1"><SafeMJ>{"\\((K\\setminus\\{0\\},\\cdot)\\)"}</SafeMJ> es un grupo abeliano con neutro <SafeMJ after=".">{"\\(1\\)"}</SafeMJ></li>
        <li className="mb-1">La multiplicación cumple la propiedad distributiva respecto de la suma.</li>
      </ul>
      Vamos, que puedes hacer las operaciones de toda la vida (sumar, restar, multiplicar y dividir) y siempre obtendrás un resultado dentro del cuerpo. Por ejemplo, <SafeMJ after=",">{"\\(\\mathbb{Q}\\)"}</SafeMJ> <SafeMJ>{"\\(\\mathbb{R}\\)"}</SafeMJ> y <SafeMJ>{"\\(\\mathbb{C}\\)"}</SafeMJ> son cuerpos, pero <SafeMJ>{"\\(\\mathbb{Z}\\)"}</SafeMJ>{" "}
      no lo es, pues solo el <SafeMJ>{"\\(1\\)"}</SafeMJ> tiene inverso multiplicativo. Así, un <b>cuerpo finito</b> es sencillamente un cuerpo con un número finito de elementos. Fácil hasta aquí.
    </p>
    <p className="mb-3">
      Recuerda también que la <b>característica</b> de un cuerpo <SafeMJ after=",">{"\\(K\\)"}</SafeMJ> denotada <SafeMJ after=",">{"\\(\\mathrm{char}(K)\\)"}</SafeMJ> se define como el menor <SafeMJ>{"\\(n\\in\\mathbb{N}\\)"}</SafeMJ> tal que:
      <SafeMJ scrollable>{"\\[\\underbrace{1+1+\\cdots+1}_{n}=0.\\]"}</SafeMJ>
      Eso si tal <SafeMJ>{"\\(n\\)"}</SafeMJ> existe, pues en caso contrario, se dice que <SafeMJ>{"\\(K\\)"}</SafeMJ> tiene característica <SafeMJ after=".">{"\\(0\\)"}</SafeMJ>
    </p>
    <p className="mb-5">
      Por ejemplo, si nos ponemos a sumar <SafeMJ after="'s">{"\\(1\\)"}</SafeMJ> en <SafeMJ after=",">{"\\(\\mathbb{Q}\\)"}</SafeMJ> nunca llegaremos a <SafeMJ after=",">{"\\(0\\)"}</SafeMJ> así que <SafeMJ after=".">{"\\(\\mathrm{char}(\\mathbb{Q})=0\\)"}</SafeMJ>{" "}
      Por el contrario, el cuerpo de restos módulo un primo <SafeMJ after=":">{"\\(p\\)"}</SafeMJ>
      <SafeMJ scrollable>{"\\[\\mathbb{F}_p=\\mathbb{Z}/p\\mathbb{Z}=\\{0,1,\\ldots,p-1\\},\\]"}</SafeMJ>
      tiene característica precisamente <SafeMJ after=",">{"\\(p\\)"}</SafeMJ> pues en este cuerpo
      <SafeMJ scrollable>{"\\[\\underbrace{1+1+\\cdots+1}_{p}=p\\equiv 0\\pmod p.\\]"}</SafeMJ>
      Ya sabes que en este tipo de anillos de congruencias, los enteros funcionan como las agujas de un reloj con <SafeMJ>{"\\(p\\)"}</SafeMJ> horas.
      Así, <SafeMJ after=",">{"\\(-2p\\)"}</SafeMJ> <SafeMJ after=",">{"\\(-p\\)"}</SafeMJ> <SafeMJ after=",">{"\\(0\\)"}</SafeMJ> <SafeMJ after=",">{"\\(p\\)"}</SafeMJ> <SafeMJ after=",">{"\\(2p\\)"}</SafeMJ> ... son equivalentes, pues todos dan
      resto <SafeMJ>{"\\(0\\)"}</SafeMJ> al dividir entre <SafeMJ after=".">{"\\(p\\)"}</SafeMJ>{" "}
      Además, el hecho de que hayamos impuesto que <SafeMJ>{"\\(p\\)"}</SafeMJ> sea primo es clave para que <SafeMJ>{"\\(\\mathbb{F}_p\\)"}</SafeMJ> sea en verdad un cuerpo, pues gracias a la identidad de Bézout, todo elemento <SafeMJ>{"\\(a\\neq 0\\)"}</SafeMJ> tiene
      inverso multiplicativo:
      <SafeMJ scrollable>{"\\[\\exists x,y\\in\\mathbb{Z}:\\;1=\\mathrm{mcd}{(a,p)}=ax+py=ax\\Longrightarrow a^{-1}=x.\\]"}</SafeMJ>
      Mira, por ejemplo, el caso de <SafeMJ after=":">{"\\(\\mathbb{F}_7\\)"}</SafeMJ>
    </p>
    <div className="overflow-x-auto md:overflow-visible overflow-y-hidden max-w-full mb-5">
      <table className="table-auto border-collapse border border-gray-300 dark:border-gray-700 w-full text-center">
        <tbody className="bg-white dark:bg-gray-900">
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2 bg-gray-100 dark:bg-gray-800"><SafeMJ>{"\\(a\\)"}</SafeMJ></th>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(0\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(3\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(4\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(5\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(6\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2 bg-gray-100 dark:bg-gray-800"><SafeMJ>{"\\(-a\\)"}</SafeMJ></th>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(0\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(6\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(5\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(4\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(3\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(1\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2 bg-gray-100 dark:bg-gray-800"><SafeMJ>{"\\(a^{-1}\\)"}</SafeMJ></th>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(-\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(4\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(5\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(3\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(6\\)"}</SafeMJ></td>
          </tr>
        </tbody>
      </table>
    </div>
    <p className="mb-3">
      De hecho, lo anterior no ha sido casualidad, pues como muestra la siguiente proposición, los números primos marcan
      una condición necesaria sobre los cuerpos finitos muy particular.
    </p>

    <p className="mb-3"> <b>Proposición 1.1.</b> La característica de un cuerpo es siempre cero o un número primo <SafeMJ after=".">{"\\(p\\)"}</SafeMJ>{" "}
    Además, si el cuerpo es finito, entonces su característica es no nula y
    su cardinalidad es <SafeMJ after=",">{"\\(p^n\\)"}</SafeMJ> para algún <SafeMJ after=".">{"\\(n\\in\\mathbb{N}\\)"}</SafeMJ>
    </p>

    <Proof>
    <p className="mb-3">
      <em>Demostración.</em> Sea <SafeMJ>{"\\(K\\)"}</SafeMJ> un cuerpo con <SafeMJ after=",">{"\\(\\mathrm{char}(K)=p\\neq 0\\)"}</SafeMJ> entonces <SafeMJ>{"\\(p\\)"}</SafeMJ> debe ser primo, pues en caso contrario tendríamos <SafeMJ>{"\\(p=ab\\)"}</SafeMJ> con <SafeMJ after=",">{"\\(1<a,b<p\\)"}</SafeMJ> y por la propiedad distributiva:
      <SafeMJ scrollable>{"\\[0=\\underbrace{1+\\cdots+1}_{p}=(\\underbrace{1+\\cdots+1}_{a})\\cdot(\\underbrace{1+\\cdots+1}_{b})\\Longrightarrow 0=a^{-1}ab=b\\;!!!\\]"}</SafeMJ>
      En particular, si <SafeMJ>{"\\(K\\)"}</SafeMJ> es finito, no puede darse <SafeMJ after=",">{"\\(\\mathrm{char}(K)=0\\)"}</SafeMJ> pues en ese caso <SafeMJ>{"\\(K\\)"}</SafeMJ> contendría infinitos elementos (uno por cada nuevo <SafeMJ>{"\\(1\\)"}</SafeMJ> que
      sumemos). Además, observa que el subcuerpo <SafeMJ>{"\\(\\mathbb{F}_p\\subset K\\)"}</SafeMJ> generado por el <SafeMJ>{"\\(1\\)"}</SafeMJ> hace de <SafeMJ>{"\\(K\\)"}</SafeMJ> un <SafeMJ after="espacio">{"\\(\\mathbb{F}_p\\text{-}\\)"}</SafeMJ> vectorial (es lo que se llama una
      extensión de cuerpos, por cierto). Al ser finito, este tiene dimensión <SafeMJ>{"\\(n\\in\\mathbb{N}\\)"}</SafeMJ> finita, por lo que <SafeMJ after=".">{"\\(|K|=|\\mathbb{F}_p|^{\\dim{K}}=p^n\\)"}</SafeMJ><span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
    </p>
    </Proof>

    <p className="mb-3">
      También existen cuerpos infinitos con característica prima, aunque el recíproco no sea cierto, como ya vimos antes con <SafeMJ after=".">{"\\(\\mathbb{Q}\\)"}</SafeMJ> Por ejemplo,
      está el clásico <SafeMJ after=",">{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> formado por los infinitos polinomios con coeficientes en <SafeMJ after=",">{"\\(\\mathbb{F}_p\\)"}</SafeMJ> el cual,
      al seguir sumando los <SafeMJ after="'s">{"\\(1\\)"}</SafeMJ> en <SafeMJ after=",">{"\\(\\mathbb{F}_p\\)"}</SafeMJ> también tiene característica <SafeMJ after=".">{"\\(p\\)"}</SafeMJ>
    </p>

    <p className="mb-3">
        Muy bien, muy interesante. Sin embargo, lo que nos interesa realmente es un poco el proceso inverso: 
        dado un primo <SafeMJ>{"\\(p\\)"}</SafeMJ> y un natural <SafeMJ after=",">{"\\(n\\)"}</SafeMJ> ¿cómo construimos un cuerpo finito
        con <SafeMJ>{"\\(p^n\\)"}</SafeMJ> elementos? Para ello disponemos, por suerte, de la siguiente proposición:
    </p>
    <p className="mb-3">
      <b>Proposición 1.2.</b> Sea <SafeMJ>{"\\(f(x)\\in\\mathbb{F}_p[x]\\)"}</SafeMJ> un polinomio irreducible de grado <SafeMJ after=",">{"\\(n\\)"}</SafeMJ> entonces el anillo cociente
      <SafeMJ scrollable>{"\\[\\mathbb{F}_p[x]\\big/\\langle f(x)\\rangle\\]"}</SafeMJ>
      es un cuerpo con exactamente <SafeMJ>{"\\(p^n\\)"}</SafeMJ> elementos.
    </p>
    <p className="mb-3">
      Por refrescar la memoria, un polinomio <SafeMJ>{"\\(f(x)\\in\\mathbb{F}_p[x]\\)"}</SafeMJ> no constante se dice <b>irreducible</b> cuando no se puede expresar como
      producto de dos polinomios de <SafeMJ>{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> no constantes. Por ejemplo, <SafeMJ>{"\\(x^2+1\\)"}</SafeMJ> es irreducible en <SafeMJ after=",">{"\\(\\mathbb{F}_3[x]\\)"}</SafeMJ>{" "}
      pues no tiene raíces en ese cuerpo (<SafeMJ after=",">{"\\(f(0)=1\\)"}</SafeMJ> <SafeMJ>{"\\(f(1)=2\\)"}</SafeMJ> y <SafeMJ after=")">{"\\(f(2)=2\\)"}</SafeMJ> y, por tanto, no se puede factorizar como producto de monomios.
      En cambio, ese mismo polinomio sí es reducible en <SafeMJ after=",">{"\\(\\mathbb{F}_5[x]\\)"}</SafeMJ> pues puede descomponerse como <SafeMJ after=".">{"\\(x^2+1=(x+2)(x+3)\\)"}</SafeMJ>
    </p>
    <p className="mb-3">
      Por otra parte, recordar que los anillos cocientes se construyen de la siguiente manera. Tomas un anillo <SafeMJ after=",">{"\\(R\\)"}</SafeMJ> en nuestro caso <SafeMJ after=",">{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ>{" "}
      y un ideal suyo <SafeMJ after=",">{"\\(I\\)"}</SafeMJ> es decir, un subgrupo aditivo de <SafeMJ>{"\\(R\\)"}</SafeMJ> que sea cerrado para la multiplicación por elementos del anillo.
      En nuestro caso, ese subgrupo especial es <SafeMJ after=",">{"\\(\\langle f(x)\\rangle\\)"}</SafeMJ> es decir, todos los polinomios divisibles por <SafeMJ after=".">{"\\(f(x)\\)"}</SafeMJ>{" "}
      Entonces, el conjunto <SafeMJ>{"\\(R/I\\)"}</SafeMJ> de clases de equivalencia <SafeMJ>{"\\(a+I\\)"}</SafeMJ> con <SafeMJ after=",">{"\\(a\\in R\\)"}</SafeMJ> donde:
      <SafeMJ scrollable>{"\\[a+I=[a]:=\\{b\\in R:a-b\\in I\\},\\]"}</SafeMJ>
      resulta ser (te animo a comprobarlo) un anillo con las siguientes operaciones:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"><SafeMJ>{"\\((a+I)+(b+I):=(a+b)+I,\\)"}</SafeMJ></li>
        <li className="mb-1"><SafeMJ>{"\\((a+I)\\cdot (b+I):=(a\\cdot b)+I.\\)"}</SafeMJ></li>
      </ul>
      Parece mucho más lioso de lo que es. En realidad, olvídate si quieres de toda esta notación y piensa en <SafeMJ>{"\\(\\mathbb{F}_p[x]/\\langle f(x)\\rangle\\)"}</SafeMJ> como
      si fueran polinomios de <SafeMJ>{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> al uso, solo que si la diferencia de dos de ellos es divisible por <SafeMJ after=",">{"\\(f(x)\\)"}</SafeMJ> entonces dices
      que esos polinomios son iguales.
    </p>
    <p className="mb-3">
      Vale. Aclarado esto, veamos ahora sí la demostración de la proposición anterior.
    </p>
    <Proof>
    <p className="mb-3">
      <em>Demostración.</em> El hecho de que <SafeMJ>{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> sea un anillo conmutativo, convierte automáticamente al anillo cociente <SafeMJ>{"\\(K=\\mathbb{F}_p[x]\\big/\\langle f(x)\\rangle\\)"}</SafeMJ> también
      en conmutativo. Por tanto, para que <SafeMJ>{"\\(K\\)"}</SafeMJ> sea un cuerpo, solo falta ver que todo elemento no nulo tiene inverso multiplicativo.
    </p>
    <p className="mb-3">
      En efecto, sea <SafeMJ>{"\\(a(x)\\in\\mathbb{F}_p[x]\\)"}</SafeMJ> no nulo con <SafeMJ after=",">{"\\(\\mathrm{gr}(a)<n=\\mathrm{gr}(f)\\)"}</SafeMJ> entonces <SafeMJ>{"\\(f\\nmid a\\)"}</SafeMJ> por una cuestión de grados.
      Además, como <SafeMJ>{"\\(f(x)\\)"}</SafeMJ> es irreducible, no tiene ningún divisor con grado entre <SafeMJ>{"\\(1\\)"}</SafeMJ> y <SafeMJ after=",">{"\\(n-1\\)"}</SafeMJ> de modo que <SafeMJ after=".">{"\\(1\\in\\mathrm{mcd}(f,a)\\)"}</SafeMJ>{" "}
      Entonces, al ser <SafeMJ>{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> un dominio euclídeo, podemos aplicar la identidad de Bézout para obtener <SafeMJ>{"\\(u(x),v(x)\\in\\mathbb{F}_p[x]\\)"}</SafeMJ> tales que:
      <SafeMJ scrollable>{"\\[a(x)\\,u(x)+f(x)\\,v(x)=1.\\]"}</SafeMJ>
      Pasando ahora al cociente, obtenemos <SafeMJ after=",">{"\\([a(x)][u(x)]=[1]\\)"}</SafeMJ> por lo que <SafeMJ after=".">{"\\([a(x)]^{-1}=[u(x)]\\)"}</SafeMJ>
    </p>
    <p className="mb-3">
      Observa, además, que si <SafeMJ after=",">{"\\(\\mathrm{gr}(a)\\ge n\\)"}</SafeMJ> simplemente aplicamos la división euclídea por <SafeMJ>{"\\(f(x)\\)"}</SafeMJ> para obtener:
      <SafeMJ scrollable>{"\\[a(x)=c(x)f(x)+r(x),\\quad\\textnormal{con }\\mathrm{gr}(r)<n,\\]"}</SafeMJ>
      de forma que <SafeMJ after=",">{"\\([a(x)]=[r(x)]\\)"}</SafeMJ> volviendo al caso anterior. De hecho, esto último prueba que cada clase <SafeMJ>{"\\([a(x)]\\)"}</SafeMJ> tiene un único representante de
      grado menor que <SafeMJ after=".">{"\\(n\\)"}</SafeMJ> Por tanto, <SafeMJ>{"\\(|K|\\)"}</SafeMJ> será el número de polinomios en <SafeMJ>{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> de
      grado menor que <SafeMJ after=",">{"\\(n\\)"}</SafeMJ> el cual es exactamente <SafeMJ>{"\\(p^n\\)"}</SafeMJ> (uno por cada elección de los <SafeMJ>{"\\(n\\)"}</SafeMJ> coeficientes en <SafeMJ after=").">{"\\(\\mathbb{F}_p\\)"}</SafeMJ>
      <span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
    </p>
    </Proof>

    <p className="mb-3">
      Antes de seguir, puede venir bien ver un ejemplo particular de todo esto. Seguro que alguno me lo agradece... Consideremos, pues,
      el polinomio irreducible de antes, <SafeMJ after=".">{"\\(f(x)=x^2+1\\in\\mathbb{F}_3[x]\\)"}</SafeMJ>{" "}
      Entonces, ahora sabemos que tenemos el cuerpo <SafeMJ after=",">{"\\(\\mathbb{F}_3[x]/\\langle x^2+1\\rangle\\)"}</SafeMJ>{" "}
      cuyos <SafeMJ>{"\\(3^2=9\\)"}</SafeMJ> elementos son:
    </p>
    <div className="overflow-x-auto md:overflow-visible overflow-y-hidden max-w-full mb-4">
      <table className="table-auto sm:table-fixed border-collapse border border-gray-300 dark:border-gray-700 w-full text-center">
        <tbody className="bg-white dark:bg-gray-900">
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2 bg-gray-100 dark:bg-gray-800"><SafeMJ>{"\\(a(x)\\)"}</SafeMJ></th>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(0\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x+1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x+2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x+1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x+2\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2 bg-gray-100 dark:bg-gray-800"><SafeMJ>{"\\(-a(x)\\)"}</SafeMJ></th>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(0\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x+2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x+1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x+2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x+1\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2 bg-gray-100 dark:bg-gray-800"><SafeMJ>{"\\(a(x)^{-1}\\)"}</SafeMJ></th>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(-\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x+2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x+1\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(x\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x+2\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 px-2 sm:px-3 py-2"><SafeMJ>{"\\(2x+1\\)"}</SafeMJ></td>
          </tr>
        </tbody>
      </table>
    </div>
    <p className="mb-3">
      Por ejemplo, ¿por qué el inverso de <SafeMJ>{"\\(2x+1\\)"}</SafeMJ> es <SafeMJ after="?">{"\\(2x+2\\)"}</SafeMJ> La clave es, como explicamos antes, que todos los
      múltiplos de <SafeMJ>{"\\(f(x)\\)"}</SafeMJ> se anulan en el anillo cociente, de forma que:
      <SafeMJ scrollable>{"\\[(2x+1)(2x+2)=4x^2+2=4(\\underbrace{x^2+1}_{\\displaystyle 0})+1=1.\\]"}</SafeMJ>
      Fíjate que he omitido los corchetes que indican las clases de equivalencia. En la práctica nadie los escribe, pues se sobreentienden y así se
      simplifica la notación.
    </p>

    <p className="mb-3">
      Genial, pues ya sabemos construir cuerpos con cardinal <SafeMJ after=".">{"\\(p^n\\)"}</SafeMJ> No ha sido tan difícil al final... Sin embargo,
      sería un error de primero de matemático quedarse aquí, pues aún quedan muchas preguntas naturales por resolver. Por ejemplo, hemos visto que,
      para construir un cuerpo con <SafeMJ>{"\\(p^n\\)"}</SafeMJ> elementos, tan solo necesitamos de un polinomio de
      grado <SafeMJ>{"\\(n\\)"}</SafeMJ> irreducible en <SafeMJ after=",">{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> pero nadie nos ha dicho que siempre podamos
      encontrar tal polinomio. Tampoco sabemos si, en caso de haber varios polinomios irreducibles candidatos, acabaríamos
      obteniendo distintos cuerpos o, por el contrario, serían "en esencia" el mismo (es decir, cuestión de renombrar variables).
    </p>
    <p className="mb-3">
      La respuesta a estas preguntas nos la da la brillantísima Teoría de Galois (¡grande Évariste!), en la cual, por desgracia, no nos da tiempo a indagar en este artículo.
      Si te animas y familiarizas un poco con los llamados cuerpos de descomposición, la función de Euler y el automorfismo de Frobenius,
      verás que no es difícil llegar a demostrar lo siguiente:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1">Para todo primo <SafeMJ>{"\\(p\\)"}</SafeMJ> y todo natural <SafeMJ after=",">{"\\(n\\)"}</SafeMJ> existe un polinomio 
        irreducible de grado <SafeMJ>{"\\(n\\)"}</SafeMJ> en <SafeMJ after=",">{"\\(\\mathbb{F}_p[x]\\)"}</SafeMJ> por lo que <b>siempre podemos construir
        cuerpos con <SafeMJ>{"\\(\\boldsymbol{p^n}\\)"}</SafeMJ> elementos</b>.
        </li>
        <li className="mb-1">
          El cuerpo de <SafeMJ>{"\\(q=p^n\\)"}</SafeMJ> elementos <b>es único salvo isomorfismo</b>, y lo denotamos como <SafeMJ>{"\\(\\mathbb{F}_{q}\\)"}</SafeMJ> o <SafeMJ after=".">{"\\(\\mathrm{GF}(q)\\)"}</SafeMJ>{" "}
          A este cuerpo se le llama, de hecho, <b>campo de Galois</b>.
        </li>
        <li className="mb-1">
          Además, el grupo multiplicativo <SafeMJ>{"\\(\\mathbb{F}_{q}^{\\times}=\\mathbb{F}_{q}\\setminus\\{0\\}\\)"}</SafeMJ> resulta ser cíclico. Es decir,
          que todos sus elementos son potencia de cierto <em>elemento primitivo</em> <SafeMJ after=".">{"\\(\\alpha\\in\\mathbb{F}_{q}^{\\times}\\)"}</SafeMJ>
        </li>
      </ul>
      Bastante fuerte, la verdad, y eso que ni hemos hablado de la relación que hay con las raíces de <SafeMJ after=".">{"\\(f(x)\\)"}</SafeMJ> Eso ya da para un <em>post</em> aparte...
    </p>
    <p className="mb-3">
      Entonces, volviendo al ejemplo de antes, la Teoría de Galois nos garantiza que <SafeMJ>{"\\(\\mathbb{F}_9\\cong \\mathbb{F}_3[x]/\\langle x^2+1\\rangle\\)"}</SafeMJ> es,
      básicamente, el único cuerpo de 9 elementos que existe. Además, resulta que <SafeMJ>{"\\(\\alpha=x+1\\)"}</SafeMJ> lo genera al completo, siendo un elemento primitivo de orden 8 del mismo:
      <SafeMJ scrollable>{`
        \\[
        \\begin{aligned}
        \\alpha^2 &= (x+1)^2=(x^2+1)+2x=2x \\\\
        \\alpha^3 &= \\alpha^2\\cdot \\alpha=2x(x+1)=2(x^2+1)+2x+1=2x+1 \\\\
        \\alpha^4 &= (\\alpha^2)^2=4x^2=(x^2+1)+2=2 \\\\
        \\alpha^5 &= \\alpha^4\\cdot \\alpha=2x+2 \\\\
        \\alpha^6 &= \\alpha^4\\cdot \\alpha^2=4x=x \\\\
        \\alpha^7 &= \\alpha^4\\cdot \\alpha^3=4x+2=x+2 \\\\
        \\alpha^8 &= (\\alpha^4)^2=4=1.
        \\end{aligned}
        \\]
      `}
      </SafeMJ>
      Listo. Ha sido un pelín denso (pido disculpas), pero ya sabemos todo lo necesario sobre cuerpos finitos para poder atacar con confianza los códigos
      Reed-Solomon, nuestros auténticos protagonistas de hoy. Así que estira la espalda y toma asiento, que ahora sí... ¡empezamos!
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">2. El código Reed-Solomon</h2>

    <p className="mb-3">
      Para entender Reed-Solomon, lo primero de todo es definir qué es un <b>código</b> (<SafeMJ after="ario)">{"\\(q\\text{-}\\)"}</SafeMJ> de longitud <SafeMJ after=",">{"\\(n\\in\\mathbb{N}\\)"}</SafeMJ> que no es ni más ni menos que cualquier 
      subconjunto <SafeMJ after=".">{"\\(\\mathcal{C}\\subset\\mathbb{F}_q^n\\)"}</SafeMJ> Si además <SafeMJ>{"\\(\\mathcal{C}\\)"}</SafeMJ> forma un subespacio vectorial de <SafeMJ after=",">{"\\(\\mathbb{F}_q^n\\)"}</SafeMJ> entonces se dice que el código es <b>lineal</b>.
      Fíjate que esta definición tiene sentido, ya que por las propiedades de los cuerpos, <SafeMJ>{"\\(\\mathbb{F}_q^n\\)"}</SafeMJ> es, de forma natural, un espacio vectorial sobre el cuerpo finito <SafeMJ after=".">{"\\(\\mathbb{F}_q\\)"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      Por otra parte, nos referimos a menudo a los vectores de <SafeMJ>{"\\(\\mathbb{F}_q^n\\)"}</SafeMJ> como <em>palabras</em>, mientras que la <em>dimensión</em> de un código lineal es, 
      cómo no, la dimensión del subespacio de toda la vida.
    </p>

    <p className="mb-3">
      Miniejemplo: <SafeMJ>{"\\(\\mathcal{C}=\\{(0,0,0),(1,0,1)\\}\\subset\\mathbb{F}_2^3\\)"}</SafeMJ> es un código lineal binario de longitud 3 y dimensión 1,
      pues las 3 coordenadas de cada palabra son <SafeMJ>{"\\(0\\)"}</SafeMJ> o <SafeMJ>{"\\(1\\)"}</SafeMJ> y basta con tomar la palabra <SafeMJ>{"\\((1,0,1)\\)"}</SafeMJ> para generarlo.
    </p>

    <p className="mb-3">
      Como podrás imaginar, existen muchísimas formas de producir códigos lineales. La que nos interesa hoy y que da lugar al <b>código Reed-Solomon</b>, se basa, en particular,
      en jugar con polinomios sobre cuerpos finitos. La idea es la siguiente:
      <ul className="list-decimal list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1 marker:font-bold">Elegimos un cuerpo finito <SafeMJ>{"\\(\\mathbb{F}_q\\)"}</SafeMJ> y dos naturales <SafeMJ after="(longitud">{"\\(k\\,\\)"}</SafeMJ> del mensaje) y <SafeMJ after="(longitud">{"\\(n\\,\\)"}</SafeMJ> de la palabra del código)
        satisfaciendo <SafeMJ after=".">{"\\(k\\le n\\le q\\)"}</SafeMJ></li>
        <li className="mb-1 marker:font-bold">Elegimos también <SafeMJ>{"\\(n\\)"}</SafeMJ> puntos distintos <SafeMJ after=".">{"\\(\\alpha_1,\\dots,\\alpha_n\\in\\mathbb{F}_q\\)"}</SafeMJ> Los que sean.</li>
        <li className="mb-1 marker:font-bold">A cada mensaje <SafeMJ>{"\\(m=(m_0,\\dots,m_{k-1})\\in\\mathbb{F}_q^k\\)"}</SafeMJ> que queramos transmitir le asociamos el polinomio <SafeMJ after=".">{"\\(m(x)=\\sum_{i=0}^{k-1}m_i x^i\\)"}</SafeMJ></li>
        <li className="mb-1 marker:font-bold">Finalmente, evaluamos dicho polinomio en los <SafeMJ>{"\\(n\\)"}</SafeMJ> puntos elegidos, obteniendo la 
        palabra del código <SafeMJ after=".">{"\\(c=(m(\\alpha_1),...,m(\\alpha_n))\\in\\mathbb{F}_q^n\\)"}</SafeMJ></li>
      </ul>
    </p>
    <p className="mb-3">
      Por tanto, formalmente definimos el código Reed-Solomon como:
      <SafeMJ scrollable>{"\\[\\mathrm{RS}(n,k):=\\bigl\\{(m(\\alpha_1),\\dots,m(\\alpha_n)):\\,m(x)\\in\\mathbb{F}_q[x]_{<k}\\bigr\\}\\subset\\mathbb{F}_q^n,\\]"}</SafeMJ>
      donde <SafeMJ>{"\\(\\mathbb{F}_q[x]_{<k}\\)"}</SafeMJ> denota los polinomios en <SafeMJ>{"\\(\\mathbb{F}_q[x]\\)"}</SafeMJ> de grado menor que <SafeMJ after=".">{"\\(k\\)"}</SafeMJ>
    </p>
    <p className="mb-3">
      ¡Ojo! La restricción <SafeMJ>{"\\(n\\le q\\)"}</SafeMJ> es clave, pues necesitamos elegir <SafeMJ>{"\\(n\\)"}</SafeMJ> puntos distintos en <SafeMJ after=",">{"\\(\\mathbb{F}_q\\)"}</SafeMJ> que solo tiene <SafeMJ>{"\\(q\\)"}</SafeMJ> elementos.
      Asimismo, es fundamental que <SafeMJ after=",">{"\\(k\\le n\\)"}</SafeMJ> pues esto garantizará, como veremos más adelante, que cada mensaje se codifique en una palabra distinta.
    </p>

    <p className="mb-3">
        Veamos un ejemplo tonto, por afianzar conceptos. Imagina que escogemos el cuerpo <SafeMJ after=",">{"\\(\\mathbb{F}_5\\)"}</SafeMJ> suponiendo que queremos enviar mensajes con solo vocales, de forma
        que <SafeMJ after=",">{"\\(a=0\\)"}</SafeMJ> <SafeMJ after=",">{"\\(e=1\\)"}</SafeMJ> <SafeMJ after=",">{"\\(i=2\\)"}</SafeMJ> <SafeMJ>{"\\(o=3\\)"}</SafeMJ> y <SafeMJ after=".">{"\\(u=4\\)"}</SafeMJ>{" "}
        Tomamos además <SafeMJ>{"\\(k=3\\)"}</SafeMJ> y <SafeMJ>{"\\(n=5\\)"}</SafeMJ> y elegimos los puntos <SafeMJ>{"\\(\\alpha_i=i-1\\)"}</SafeMJ> para <SafeMJ after=".">{"\\(i=1,2,3,4,5\\)"}</SafeMJ>{" "}
        Entonces, si quisiéramos transmitir el mensaje <SafeMJ after=",">{"\\(\\textrm{`}ouo\\textrm{'}\\)"}</SafeMJ> el proceso de codificación sería:
        <SafeMJ scrollable>{`
          \\[
          \\begin{gathered}
          m=(o,u,o)=(3,4,3)\\longrightarrow m(x)=3+4x+3x^2 \\\\[5pt]
          \\longrightarrow c=(m(0),m(1),m(2),m(3),m(4))=(3,10,23,42,67)\\equiv(3,0,3,2,2).
          \\end{gathered}
          \\]
        `}</SafeMJ>
    </p>
    <p className="mb-3">
      Genial, con esto la cosa queda clara. Sin embargo, hay un detalle que nos hemos saltado. Hemos dicho que el código Reed-Solomon es un ejemplo de código lineal, pero no hemos demostrado que lo sea, es decir,
      nos falta probar el siguiente enunciado:
    </p>
    <p className="mb-3">
      <b>Proposición 2.1.</b> <SafeMJ>{"\\(\\mathrm{RS}(n,k)\\)"}</SafeMJ> es un <SafeMJ after="subespacio">{"\\(\\mathbb{F}_q\\text{-}\\)"}</SafeMJ> vectorial de <SafeMJ>{"\\(\\mathbb{F}_q^n\\)"}</SafeMJ> de dimensión <SafeMJ after=".">{"\\(k\\)"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      Para ello, sin embargo, necesitamos de un par de resultados previos sobre polinomios bastante elementales. Por hacer las cosas bien,
      veámoslos sobre un cuerpo <SafeMJ>{"\\(K\\)"}</SafeMJ> arbitrario, que siempre viene bien generalizar. Aquí va el primero:
    </p>

    <p className="mb-3">
    <b>Lema 2.2.</b> Sea <SafeMJ>{"\\(m(x)\\in K[x]\\)"}</SafeMJ> un polinomio no nulo de grado <SafeMJ after=",">{"\\(k\\)"}</SafeMJ>{" "}
      entonces <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> tiene a lo sumo <SafeMJ>{"\\(k\\)"}</SafeMJ> raíces distintas en <SafeMJ after=".">{"\\(K\\)"}</SafeMJ>
    </p>

    <Proof>
      <p className="mb-3">
        <em>Demostración.</em> Procedamos por inducción sobre <SafeMJ after=".">{"\\(k\\)"}</SafeMJ>
      </p>
      <p className="mb-3">
        Si <SafeMJ after=",">{"\\(k=0\\)"}</SafeMJ> <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> es una constante no nula y no tiene raíces, luego se cumple el enunciado trivialmente.
      </p>
      <p className="mb-3">
        Si <SafeMJ>{"\\(k\\ge 1\\)"}</SafeMJ> y simplemente <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> no tiene raíces, de nuevo se cumple trivialmente el lema.
        Por el contrario, si <SafeMJ>{"\\(\\alpha\\in K\\)"}</SafeMJ> es una raíz de <SafeMJ after=",">{"\\(m(x)\\)"}</SafeMJ> entonces
        aplicamos la división euclídea en <SafeMJ>{"\\(K[x]\\)"}</SafeMJ> por <SafeMJ after=",">{"\\(x-\\alpha\\)"}</SafeMJ> obteniendo:
        <SafeMJ scrollable>{"\\[m(x)=q(x)(x-\\alpha)+r,\\quad \\textnormal{con } r\\in K.\\]"}</SafeMJ>
        Evaluando en <SafeMJ>{"\\(\\alpha\\)"}</SafeMJ> se obtiene <SafeMJ after=",">{"\\(r=0\\)"}</SafeMJ> así que:
        <SafeMJ scrollable>{"\\[m(x)=q(x)(x-\\alpha),\\quad \\textnormal{con } \\mathrm{gr}(q)<n.\\]"}</SafeMJ>
        Como, por hipótesis de inducción, <SafeMJ>{"\\(q(x)\\)"}</SafeMJ> tiene a lo sumo <SafeMJ>{"\\(k-1\\)"}</SafeMJ> raíces distintas en <SafeMJ after=",">{"\\(K\\)"}</SafeMJ>{" "}
        entonces <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> tendrá como mucho <SafeMJ>{"\\(k\\)"}</SafeMJ> raíces distintas (las <SafeMJ>{"\\(k-1\\)"}</SafeMJ> de <SafeMJ>{"\\(q(x)\\)"}</SafeMJ> y <SafeMJ after=").">{"\\(\\alpha\\)"}</SafeMJ>
        <span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
      </p>
    </Proof>
  
    <p className="mb-3">
      Fíjate que de este resultado tan tonto se deduce, automáticamente, el siguiente corolario:
    </p>
  
    <p className="mb-3">
      <b>Corolario 2.3.</b> Si <SafeMJ>{"\\(m(x)\\in K[x]_{<k}\\)"}</SafeMJ> tiene <SafeMJ>{"\\(k\\)"}</SafeMJ> raíces distintas en <SafeMJ after=",">{"\\(K\\)"}</SafeMJ>{" "}
      entonces <SafeMJ after=".">{"\\(m(x)\\equiv 0\\)"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      Y ya con esto podemos proceder a demostrar la linealidad y dimensión de Reed-Solomon:
    </p>
    
    <Proof>
    <p className="mb-3">
      <em>Demostración de <SafeMJ after=".">{"\\(\\textit{(2.1)}\\)"}</SafeMJ></em> Como <SafeMJ>{"\\(\\mathbb{F}_q[x]_{<k}\\)"}</SafeMJ> es un <SafeMJ after="espacio">{"\\(\\mathbb{F}_q\\text{-}\\)"}</SafeMJ> vectorial de dimensión <SafeMJ>{"\\(k\\)"}</SafeMJ> (observa
      que <SafeMJ>{"\\(\\{1,x,x^2,\\dots,x^{k-1}\\}\\)"}</SafeMJ> es una base), entonces tenemos la siguiente aplicación lineal:
      <SafeMJ scrollable>{"\\[\\mathrm{ev}:\\;\\mathbb{F}_q[x]_{<k}\\longrightarrow\\mathbb{F}_q^n,\\quad m(x)\\longmapsto(m(\\alpha_1),\\dots,m(\\alpha_n)).\\]"}</SafeMJ>
      Esto hace automáticamente de <SafeMJ>{"\\(\\mathrm{RS}(n,k)=\\mathrm{Im}(\\mathrm{ev})\\)"}</SafeMJ> un <SafeMJ after="subespacio">{"\\(\\mathbb{F}_q\\text{-}\\)"}</SafeMJ> vectorial de <SafeMJ after=".">{"\\(\\mathbb{F}_q^n\\)"}</SafeMJ>{" "}
      
      Además, el núcleo de <SafeMJ>{"\\(\\mathrm{ev}\\)"}</SafeMJ> está formado por los polinomios de grado menor que <SafeMJ>{"\\(k\\)"}</SafeMJ> con <SafeMJ>{"\\(\\alpha_1,\\dots,\\alpha_n\\)"}</SafeMJ> como raíces distintas.
      Como <SafeMJ after=",">{"\\(n\\ge k\\)"}</SafeMJ> entonces por el corolario anterior, el núcleo es trivial y <SafeMJ>{"\\(\\mathrm{ev}\\)"}</SafeMJ> inyectiva. De este modo, usando el famoso teorema de rango-nulidad, tenemos
      que <SafeMJ after=".">{"\\(\\dim{\\mathrm{RS}(n,k)}=\\dim{\\mathbb{F}_q[x]_{<k}}=k\\)"}</SafeMJ><span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
    </p>
    </Proof>

    <p className="mb-3">
      Un detalle importante: al ver que la aplicación de evaluación era inyectiva, en realidad estábamos diciendo algo más profundo. Si <SafeMJ>{"\\(\\mathrm{ev}\\)"}</SafeMJ> no fuera inyectiva,
      eso significaría que habría dos mensajes distintos que se codificarían en la misma palabra, lo cual sería un incordio para el receptor. ¿Cómo sabría entonces de qué mensaje de los dos se trata la palabra recibida?
      Dicho de otro modo, la inyectividad de <SafeMJ>{"\\(\\mathrm{ev}\\)"}</SafeMJ> nos da la tranquilidad de que, siempre que no sucedan errores durante la transmisión,
      podremos decodificar inequívocamente. Por cierto, ¿habría pasado lo mismo si no hubiéramos impuesto <SafeMJ after="?">{"\\(k\\le n\\)"}</SafeMJ> Te dejo pensarlo...
    </p>

    <p className="mb-3">
      Ahora sí, todo en orden. Sigamos, pues, con más propiedades de este bicho que hemos construido. Por ejemplo, aprovechando la aplicación de evaluación que hemos definido en la demostración anterior,
      podemos reescribir el proceso de codificación de Reed-Solomon como la multiplicación de un vector por una matriz.
    </p>

    <p className="mb-3">
      En efecto, sea <SafeMJ>{"\\(V\\in\\mathbb{F}_q^{n\\times k}\\)"}</SafeMJ> la matriz de <SafeMJ>{"\\(\\mathrm{ev}\\)"}</SafeMJ> en la base <SafeMJ>{"\\(\\{1,x,x^2,\\dots,x^{k-1}\\}\\)"}</SafeMJ> de <SafeMJ after=",">{"\\(\\mathbb{F}_q[x]_{<k}\\)"}</SafeMJ> sabemos
      del álgebra lineal que para conocer sus columnas basta con evaluar la aplicación en los elementos de la base. Es decir:
      <SafeMJ scrollable>{"\\[V_{ij}=\\mathrm{ev}(x^{j-1})_i=\\alpha_i^{j-1},\\]"}</SafeMJ>
      o, en forma matricial:
      <SafeMJ scrollable>{"\\[V=\\begin{pmatrix}1&\\alpha_1&\\alpha_1^2&\\cdots&\\alpha_1^{k-1}\\\\ 1&\\alpha_2&\\alpha_2^2&\\cdots&\\alpha_2^{k-1}\\\\ \\vdots&\\vdots&\\vdots&\\ddots&\\vdots\\\\ 1&\\alpha_n&\\alpha_n^2&\\cdots&\\alpha_n^{k-1}\\end{pmatrix}\\in\\mathbb{F}_q^{n\\times k}.\\]"}</SafeMJ>
      A esta matriz se la conoce como <em>matriz de Vandermonde</em> (la versión rectangular). Un nombre algo espeluznante, pero bueno, así se llamaba el hombre. Seguramente la recuerdes del bachillerato, pues aparecía en el contexto del cálculo de determinantes.
    </p>

    <p className="mb-3">
      Nosotros sabemos, por la proposición anterior, que esta matriz tiene rango <SafeMJ>{"\\(k\\)"}</SafeMJ> y, además, podemos usarla
      para simplificar el proceso de codificación de Reed-Solomon a:
      <SafeMJ scrollable>{"\\[c=Vm.\\tag{2.4}\\]"}</SafeMJ>
      En muchas ocasiones, la expresión anterior suele escribirse por filas, es decir, así: <SafeMJ scrollable>{"\\[c^\\top=m^\\top G,\\tag{2.5}\\]"}</SafeMJ>
      siendo <SafeMJ>{"\\(G=V^\\top\\)"}</SafeMJ> la llamada <b>matriz generadora</b> del código. Recibe este nombre porque, por las propiedades
      de las aplicaciones lineales, sus filas (las columnas de <SafeMJ after=")">{"\\(V\\)"}</SafeMJ> forman una base de <SafeMJ after=".">{"\\(\\mathrm{RS}(n,k)\\)"}</SafeMJ>
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">3. Decodificación sin errores</h2>
    
    <p className="mb-3">
      Ahora que ya sabemos cómo codificar mensajes con Reed-Solomon, el siguiente paso natural es aprender a decodificarlos.
      Es decir, por ahora conocemos el fácil papel del emisor, que simplemente multiplica por una matriz y se lava las manos, pero aún nos queda por 
      desgranar el del receptor. El de este último, por desgracia, no acostumbra a ser tan fácil, pues durante la transmisión pueden suceder anomalías como
      las siguientes:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"><b>Borrado:</b> una componente de la palabra transmitida se pierde por el camino.</li>
        <SafeMJ scrollable>{"\\[c=(3,0,3,2,2)\\longrightarrow r=(3,\\square,3,2,2)\\]"}</SafeMJ>
        <li className="mb-1"><b>Error:</b> una componente de la palabra transmitida se altera durante el camino.</li>
        <SafeMJ scrollable>{"\\[c=(3,0,3,2,2)\\longrightarrow r=(3,0,2,2,2)\\]"}</SafeMJ>
      </ul>
      Observa, importante, que en el primer caso el receptor conoce la posición que se ha visto afectada durante la transmisión, lo cual, a priori,
      es un misterio en el caso de un error.
    </p>

    <p className="mb-3">
      Vale, pues pensando ya en el <em>modus operandi</em> del receptor, pongámonos primero en el caso fácil. Supongamos que no sucedieron errores ni borrados durante la transmisión de la palabra, es decir, que todo salió perfecto.
      Entonces, lo primero que se nos ocurre es simplemente darle la vuelta a la expresión <SafeMJ after=",">{"\\((2.4)\\)"}</SafeMJ> la misma que usó el emisor para codificar el mensaje. Pensando 
      en <SafeMJ>{"\\(m\\)"}</SafeMJ> como un vector de incógnitas y en <SafeMJ>{"\\(c\\)"}</SafeMJ> como un vector de términos independientes, nos damos cuenta de que estamos simplemente ante
      un sistema lineal de <SafeMJ>{"\\(n\\)"}</SafeMJ> ecuaciones y <SafeMJ>{"\\(k\\)"}</SafeMJ> incógnitas, los cuales dominamos con absoluta soltura. Es más, esto sirve, si te fijas, para cualquier código lineal que
      queramos, no solo para Reed-Solomon. Lo único es hallar una matriz generadora del código, plantear el sistema <SafeMJ>{"\\((2.5)\\)"}</SafeMJ> y resolver...
    </p>
    <p className="mb-3">
      Okey, fácil, pero es cierto que este era el caso bueno... ¿Qué pasa si, por ejemplo, la palabra sufre borrados? Tranquilidad.
      Se puede demostrar fácilmente por inducción que el determinante de Vandermonde, es decir, el de cualquier submatriz <SafeMJ>{"\\(V[i_1,\\ldots,i_k;:]\\)"}</SafeMJ> de <SafeMJ>{"\\(V\\)"}</SafeMJ> de
      tamaño <SafeMJ>{"\\(k\\times k\\)"}</SafeMJ> es:
      <SafeMJ scrollable>{"\\[\\prod_{1\\le p<q\\le k}(\\alpha_{i_q}-\\alpha_{i_p}),\\]"}</SafeMJ>
      el cual es siempre no nulo por ser todos los <SafeMJ after="'s">{"\\(\\alpha_i\\)"}</SafeMJ> distintos dos a dos. Esto implica que,
      nos quedemos con las <SafeMJ>{"\\(k\\)"}</SafeMJ> ecuaciones de <SafeMJ>{"\\((2.4)\\)"}</SafeMJ> que nos quedemos, el sistema seguirá teniendo la misma solución única.
      Fíjate que esto, lo que quiere decir en realidad, es que con Reed-Solomon <b>nos podemos permitir sufrir hasta <SafeMJ>{"\\(\\boldsymbol{n-k}\\)"}</SafeMJ> borrados en una palabra</b>, pues podríamos
      descartar sin problema las ecuaciones incompletas y resolver el sistema con las restantes. ¡Maravilloso! Y obviamente, cuanto mayor sea la diferencia entre <SafeMJ>{"\\(n\\)"}</SafeMJ> y <SafeMJ after=",">{"\\(k\\)"}</SafeMJ>{" "}
      más resiliente será el código (aunque también más pesado, claro).
    </p>
    <p className="mb-3"> 
      Como te habrás dado cuenta, esto ya no aplica a cualquier
      código lineal, como ocurría antes, pues no es cierto que toda matriz generadora de rango <SafeMJ>{"\\(k\\)"}</SafeMJ> tenga todos sus menores de orden <SafeMJ>{"\\(k\\)"}</SafeMJ> no nulos,
      sino que únicamente se sabe que al menos uno de ellos es no nulo. Luego dependiendo de las componentes que se borren, el sistema podría tener varias soluciones...
      Lo otro, más fuerte, ya es una particularidad propia de Reed-Solomon.
    </p>

    <p className="mb-3">
      Guay, entonces, ¿todo se reduce al final a resolver un sistema lineal? ¿Ya está? Bueno, no del todo... Ya sabes que métodos como la eliminación de Gauss, el clásico de ir haciendo
      una escalera de ceros, tienen, salvo casos especiales, un coste computacional de <SafeMJ after=".">{"\\(O(k^3)\\)"}</SafeMJ> Teniendo en cuenta
      que hay situaciones, como en un DVD, por ejemplo, donde la cantidad de datos es bastante considerable y que se busca acercarse al
      tiempo real, es casi mandatorio buscar algo más eficiente.
    </p>

    <p className="mb-3">
      Por suerte, en el caso de Reed-Solomon, existe un mecanismo matemático muy sencillo basado en polinomios que consigue reducir el coste computacional anterior.
      La clave reside en la siguiente proposición:
    </p>

    <p className="mb-3">
      <b>Proposición 3.1.</b> Sea <SafeMJ>{"\\(K\\)"}</SafeMJ> un cuerpo y sean los puntos <SafeMJ>{"\\((\\alpha_1,y_1),\\dots,(\\alpha_k,y_k)\\in K\\times K\\)"}</SafeMJ> con <SafeMJ after=":">{"\\(\\alpha_i\\neq\\alpha_j,\\,\\forall i\\neq j\\)"}</SafeMJ>
      <SafeMJ scrollable>{"\\[L(x)=\\sum_{i=1}^{k}y_i\\prod_{j\\neq i}^{k}\\frac{x-\\alpha_j}{\\alpha_i-\\alpha_j}\\]"}</SafeMJ>
      es el único polinomio de <SafeMJ>{"\\(K[x]_{<k}\\)"}</SafeMJ> satisfaciendo <SafeMJ after=".">{"\\(L(\\alpha_i)=y_i,\\,\\forall i=1,\\ldots,k\\)"}</SafeMJ>
    </p>
    <p className="mb-3">
      <SafeMJ>{"\\(L(x)\\)"}</SafeMJ> recibe el nombre especial de <b>polinomio interpolador de Lagrange</b>. Interpolar, en este caso,
      significa "pasar por los puntos". Veamos primero su demostración, la cual es básicamente comprobar que todo cuadre, y luego cómo podemos
      usarlo en nuestro caso particular para decodificar más rápido.
    </p>

    <Proof>
    <p className="mb-3">
      <em>Demostración.</em> Para cada <SafeMJ after=",">{"\\(i\\in\\{1,\\ldots,k\\}\\)"}</SafeMJ> definimos el siguiente polinomio auxiliar de <SafeMJ after=":">{"\\(K[x]\\)"}</SafeMJ>
      <SafeMJ scrollable>{"\\[\\ell_i(x):=\\prod_{j\\neq i}^{k}\\frac{x-\\alpha_j}{\\alpha_i-\\alpha_j}=\\frac{x-\\alpha_1}{\\alpha_i-\\alpha_1}\\cdots\\frac{x-\\alpha_{i-1}}{\\alpha_i-\\alpha_{i-1}}\\cdot\\frac{x-\\alpha_{i+1}}{\\alpha_i-\\alpha_{i+1}}\\cdots\\frac{x-\\alpha_k}{\\alpha_i-\\alpha_k},\\]"}</SafeMJ>
      el cual tiene grado <SafeMJ>{"\\(k-1\\)"}</SafeMJ> y satisface, para todo <SafeMJ after=":">{"\\(s\\in\\{1,\\ldots,k\\}\\)"}</SafeMJ>
      <SafeMJ scrollable>{"\\[\\ell_i(\\alpha_s)=\\delta_{is}=\\begin{cases}1, & i=s,\\\\ 0, & i\\neq s.\\end{cases}\\]"}</SafeMJ>
      Entonces, <SafeMJ>{"\\(L(x)=\\sum_{i=1}^n y_i \\ell_i(x)\\)"}</SafeMJ> tiene grado menor que <SafeMJ>{"\\(k\\)"}</SafeMJ> y, evaluándolo en los <SafeMJ after="'s,">{"\\(\\alpha_s\\)"}</SafeMJ> se ve que interpola a los puntos dados.
    </p>
    <p className="mb-3">
      Respecto a la unicidad, sean <SafeMJ>{"\\(L_1(x),L_2(x)\\in K[x]_{<k}\\)"}</SafeMJ> interpolando a los puntos dados, entonces <SafeMJ>{"\\(D(x)=L_1(x)-L_2(x)\\)"}</SafeMJ> tiene grado menor que <SafeMJ>{"\\(k\\)"}</SafeMJ> y
      se anula en los <SafeMJ>{"\\(k\\)"}</SafeMJ> puntos distintos <SafeMJ after=".">{"\\(\\alpha_1,\\ldots,\\alpha_k\\)"}</SafeMJ> Así, por el corolario <SafeMJ after=",">{"\\((2.3)\\)"}</SafeMJ> necesariamente se tiene
      que <SafeMJ after=",">{"\\(D(x)\\equiv 0\\)"}</SafeMJ> es decir, que <SafeMJ after=".">{"\\(L_1(x)\\equiv L_2(x)\\)"}</SafeMJ>
      <span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
    </p>
    </Proof>

    <p className="mb-3">
      La proposición anterior así escrita no dice gran cosa. Sin embargo, al leerla al revés, obtenemos la idea ganadora:
      un polinomio de grado menor que <SafeMJ>{"\\(k\\)"}</SafeMJ> está completamente determinado por su valor en <SafeMJ>{"\\(k\\)"}</SafeMJ> puntos
      cualesquiera. Por tanto, si lo evaluamos en <SafeMJ>{"\\(n\\ge k\\)"}</SafeMJ> puntos, los <SafeMJ>{"\\(n-k\\)"}</SafeMJ> valores
      extra son redundantes y podrán ser perdidos durante la transmisión. Es, en realidad, la misma conclusión a la que llegamos antes, solo que sin pasar
      por matrices, determinante de Vandermonde y demás.
    </p>

    <p className="mb-3">
      Lo potente, sin embargo, no viene de la conclusión, sino del proceso. Resulta que calcular el polinomio de Lagrange, y por tanto decodificar la palabra
      (recuerda que sus coeficientes serán los del mensaje original), es más eficiente que plantear el sistema lineal que vimos antes. Fíjate que para hallar
      los coeficientes del polinomio interpolador, hay que realizar <SafeMJ>{"\\(k\\)"}</SafeMJ> sumas de <SafeMJ>{"\\(k-1\\)"}</SafeMJ> productos, luego el coste
      total se reduce a <SafeMJ after=".">{"\\(O(k^2)\\)"}</SafeMJ> Bastante mejor... Incluso hay métodos aún más rápidos basados en la transformada discreta de Fourier,
      pero bueno, creo que eso ya se nos va ligeramente de las manos.
    </p>

    <p className="mb-3">
      Por tanto, por acabar con esta parte, veamos un ejemplo sencillo. Tomemos el caso de <SafeMJ>{"\\(\\textrm{`}ouo\\textrm{'}\\)"}</SafeMJ> de la segunda sección
      y supongamos el borrado de antes:
      <SafeMJ scrollable>{"\\[m=(3,4,3)\\longrightarrow c=(3,0,3,2,2)\\longrightarrow r=(3,\\square,3,2,2).\\]"}</SafeMJ>
      Recuerda que teníamos <SafeMJ>{"\\(q=n=5\\)"}</SafeMJ> y <SafeMJ after=",">{"\\(k=3\\)"}</SafeMJ> luego podíamos permitirnos en realidad hasta <SafeMJ>{"\\(n-k=2\\)"}</SafeMJ> borrados.
      Vamos, que nos sobra incluso una coordenada en este caso. Entonces, tomando, por ejemplo, las últimas 3 coordenadas de la palabra recibida, el polinomio interpolador nos queda:
      <SafeMJ scrollable>{`
        \\[
        \\begin{aligned}
        m(x) &= 3\\cdot\\frac{x-3}{2-3}\\cdot\\frac{x-4}{2-4} + 2\\cdot\\frac{x-2}{3-2}\\cdot\\frac{x-4}{3-4} + 2\\cdot\\frac{x-2}{4-2}\\cdot\\frac{x-3}{4-3} \\\\[5pt]
        &= 4\\cdot(x-3)\\cdot(x-4) + 3\\cdot(x-2)\\cdot(x-4) + (x-2)\\cdot(x-3) \\\\[5pt]
        &= (4x^2+2x+3) + (3x^2+2x+4) + (x^2+1) \\\\[5pt]
        &= 3x^2+4x+3,
        \\end{aligned}
        \\]
      `}</SafeMJ>
      y hemos recuperado, así de fácil, el mensaje original.

    </p>

    <p className="mb-3">
      ¡Ah! Una última cosa antes de pasar a lo siguiente. No lo he mencionado antes porque me parecía que se daba por sentado, pero por si acaso lo digo.
      Evidentemente, todos los "parámetros de configuración" del código usado (por ejemplo, en Reed-Solomon: <SafeMJ after=",">{"\\(n\\)"}</SafeMJ> <SafeMJ after=",">{"\\(q\\)"}</SafeMJ> <SafeMJ after=",">{"\\(k\\)"}</SafeMJ>{" "}
      los <SafeMJ after="'s,">{"\\(\\alpha_i\\)"}</SafeMJ> ...) son abiertamente conocidos por el emisor y el receptor. O sea, no confundas 'codificar' con 'cifrar', eh, aquí no hay nada de criptografía ni secretos.
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">4. Distancia mínima y capacidad correctora</h2>

    <p className="mb-3">
      Hemos aprendido a decodificar, encima de varias maneras, palabras del código perfectamente recibidas o con una cantidad razonable de borrados.
      Subamos ahora un poco el listón y empecemos a pensar qué pasa cuando estas incluyen alteraciones en sus componentes.
      Para ello, necesitamos introducir antes algunos conceptos teóricos clave en la detección y corrección de errores.
    </p>
    <p className="mb-3">
      Primero, sean dos palabras <SafeMJ after=",">{"\\(u,v\\in\\mathbb{F}_q^n\\)"}</SafeMJ> definimos su <b>distancia de Hamming</b> como la cantidad de coordenadas en las que difieren. Es decir:
      <SafeMJ scrollable>{"\\[d_H(u,v):=\\#\\{i:u_i\\neq v_i\\}.\\]"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      En el caso de un código <SafeMJ after=",">{"\\(\\mathcal{C}\\subset\\mathbb{F}_q^n\\)"}</SafeMJ> su <b>distancia mínima de Hamming</b> se define, evidentemente, como la mínima distancia de Hamming entre dos palabras distintas del mismo:
      <SafeMJ scrollable>{"\\[d(\\mathcal{C}):=\\min\\{d_H(c,c'):c,c'\\in\\mathcal{C},\\;c\\neq c'\\}.\\]"}</SafeMJ>
    </p>

    <p className="mb-3">
      Nota que en las definiciones anteriores hemos usado la palabra 'distancia' muy libremente. Lo suyo es comprobar que verdaderamente se cumplen las propiedades de este objeto matemático. Recuerda:
      no negatividad, identidad, simetría y desigualdad triangular, lo cual te dejo como ejercicio para no alargar el artículo. Verás que no es difícil, sobre todo la primera distancia.
    </p>

    <p className="mb-3">
      Vale, pero ¿por qué son importantes estas distancias? Pues fíjate. Imagina que estamos comunicándonos con un código <SafeMJ>{"\\(\\mathcal{C}\\)"}</SafeMJ> con distancia
      mínima <SafeMJ>{"\\(d\\)"}</SafeMJ> y que durante la transmisión de una de sus palabras, <SafeMJ after=",">{"\\(c\\)"}</SafeMJ> se alteran <SafeMJ>{"\\(e\\ge 1\\)"}</SafeMJ> de sus componentes.
      Entonces, siempre que <SafeMJ after=",">{"\\(e<d\\)"}</SafeMJ> será imposible que la palabra resultante <SafeMJ>{"\\(r\\)"}</SafeMJ> coincida con otra del código,
      pues en ese caso se tendría que <SafeMJ after=",">{"\\(d_H(c,r)=e<d\\)"}</SafeMJ> en contradicción con la minimalidad de <SafeMJ after=".">{"\\(d\\)"}</SafeMJ>{" "}
      Y así, como receptores, podremos afirmar que la palabra ha sufrido errores de transmisión.
    </p>

    <p className="mb-3">
      Pero es que hay más. Supón algo un pelín más restrictivo, que <SafeMJ after=",">{"\\(e\\le t=\\lfloor(d-1)/2\\rfloor\\)"}</SafeMJ> y considera cualquier otra palabra <SafeMJ>{"\\(c'\\neq c\\)"}</SafeMJ> del código. Entonces, no puede
      darse que <SafeMJ after=",">{"\\(d_H(c',r) \\le d_H(c,r)\\)"}</SafeMJ> pues por la desigualdad triangular se tendría:
      <SafeMJ scrollable>{"\\[d\\le d_H(c,c')\\le d_H(c,r)+d_H(r,c')\\le 2 d_H(c,r)=2e\\le d-1\\;!!!\\]"}</SafeMJ>
      Es decir, que necesariamente <SafeMJ after=",">{"\\(d_H(c',r) > d_H(c,r)\\)"}</SafeMJ> o lo que es lo mismo: ¡la menor distancia a la palabra recibida nos la da, inequívocamente, la palabra original! Eso es genial, 
      porque significa que podríamos recuperarnos del error de transmisión buscando la palabra válida (es decir, del código) más cercana a la recibida:
      <SafeMJ scrollable>{"\\[c=\\underset{c'\\in\\mathcal{C}}{\\arg\\min}\\; d_H(r,c').\\tag{4.1}\\]"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      Resumiendo, acabamos de ver que para cualquier código con distancia mínima <SafeMJ after=",">{"\\(d\\)"}</SafeMJ> podemos:
    <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
      <li className="mb-1"><b>Detectar palabras con hasta <SafeMJ>{"\\(\\boldsymbol{d-1}\\)"}</SafeMJ> errores</b>.</li>
      <li className="mb-1"><b>Corregir palabras con hasta <SafeMJ>{"\\(\\boldsymbol{t=\\lfloor(d-1)/2\\rfloor}\\)"}</SafeMJ> errores</b>.</li>
    </ul>
    Fuera del primer rango, lamentablemente, podríamos no saber si la palabra recibida es la original o si, por el contrario,
    mutó tanto por el camino que se convirtió en otra palabra válida del código. Un fastidio, ya lo sé. Y lo mismo pasa con el segundo.
    Si <SafeMJ after=",">{"\\(t<e<d\\)"}</SafeMJ> entonces sabremos que la palabra está corrupta, pero la palabra del código más cercana podría no ser la original.
    </p>

    <p className="mb-3">
    Por tanto, buscaremos usar siempre códigos con la mayor distancia mínima posible, pues más amplio será entonces nuestro rango de detectar y corregir errores.
    Eso sí, hasta un tope insuperable, tal y como muestra la siguiente proposición:
    </p>

    <p className="mb-3">
      <b>Proposición 4.2.</b> Todo código <SafeMJ>{"\\(\\mathcal{C}\\subset\\mathbb{F}_q^n\\)"}</SafeMJ> con distancia mínima <SafeMJ>{"\\(d\\)"}</SafeMJ> satisface:
      <SafeMJ scrollable>{"\\[|\\mathcal{C}|\\le q^{n-d+1}.\\]"}</SafeMJ>
      A esta cota superior se la conoce como <b>cota de Singleton</b>, y a un código que la alcanza se le llama <b>MDS</b> (por el inglés, <em>Maximum Distance Separable</em>).
    </p>

    <Proof>
    <p className="mb-3">
      <em>Demostración.</em> Consideramos la proyección:
      <SafeMJ scrollable>{"\\[\\pi:\\;\\mathcal{C}\\longrightarrow\\mathbb{F}_q^{n-d+1},\\quad (c_1,\\dots,c_n)\\longmapsto(c_1,\\dots,c_{n-d+1}),\\]"}</SafeMJ>
      la cual es inyectiva, pues si tuviéramos dos palabras <SafeMJ>{"\\(c\\neq c'\\)"}</SafeMJ> con <SafeMJ after=",">{"\\(\\pi(c)=\\pi(c')\\)"}</SafeMJ> estas diferirían a lo sumo en las últimas <SafeMJ>{"\\(d-1\\)"}</SafeMJ> coordenadas, 
      en contradicción con <SafeMJ after=".">{"\\(d_H(c,c')\\ge d\\)"}</SafeMJ> Por tanto, comparando cardinales, obtenemos <SafeMJ after=".">{"\\(|\\mathcal{C}|\\le |\\mathbb{F}_q^{n-d+1}|= q^{n-d+1}\\)"}</SafeMJ><span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
    </p>
    </Proof>

    <p className="mb-3">
      Observa, además, que en el caso de un código lineal de dimensión <SafeMJ after=",">{"\\(k\\)"}</SafeMJ> la expresión anterior se puede simplificar muy gratamente,
      pues <SafeMJ>{"\\(q^k=|\\mathcal{C}|\\le q^{n-d+1}\\)"}</SafeMJ> se da si y solo si:
      <SafeMJ scrollable>{"\\[d\\le n-k+1,\\]"}</SafeMJ>
      y entonces, en caso de igualdad, se pueden corregir palabras con hasta <SafeMJ>{"\\(t=\\bigl\\lfloor\\tfrac{n-k}{2}\\bigr\\rfloor\\)"}</SafeMJ> errores.
    </p>

    <p className="mb-3">
      En cuanto al código Reed-Solomon, que es el caso que realmente nos interesa, hay buenas noticias. La desigualdad anterior es en realidad una igualdad, es decir,
      que <b>Reed-Solomon es MDS</b>. Para verlo, basta tomar cualquier par de palabras <SafeMJ>{"\\(c,c'\\in\\mathrm{RS}(n,k)\\)"}</SafeMJ> distintas. Entonces, sean <SafeMJ>{"\\(m_1(x),m_2(x)\\in\\mathbb{F}_q[x]_{<k}\\)"}</SafeMJ> los
      polinomios asociados respectivamente, consideramos su diferencia <SafeMJ after=".">{"\\(r(x)=m_1(x)-m_2(x)\\neq 0\\)"}</SafeMJ>{" "}
      Por el lema <SafeMJ after=",">{"\\((2.2)\\)"}</SafeMJ> <SafeMJ>{"\\(r(x)\\)"}</SafeMJ> tiene como mucho <SafeMJ>{"\\(k-1\\)"}</SafeMJ> raíces distintas
      en <SafeMJ after=",">{"\\(\\mathbb{F}_q\\)"}</SafeMJ> por lo que entre <SafeMJ>{"\\(r(\\alpha_1),\\dots,r(\\alpha_n)\\)"}</SafeMJ> hay a lo sumo <SafeMJ>{"\\(k-1\\)"}</SafeMJ> ceros. Esto implica:
      <SafeMJ scrollable>{"\\[d_H(c,c')=\\#\\{i: m_1(\\alpha_i)\\neq m_2(\\alpha_i)\\}\\ge n-(k-1)=n-k+1,\\]"}</SafeMJ>
      y por tanto, tomando el mínimo:
      <SafeMJ scrollable>{"\\[d=\\min_{c\\neq c'}{d_H(c,c')} \\ge n-k+1.\\]"}</SafeMJ>
    </p>

    <p className="mb-3">
      Y ya, por cubrir todos los casos: ¿qué pasa si la palabra recibida contiene tanto errores como borrados? Bueno, pues básicamente se tiene que
      cada borrado "equivale a dos errores", por así decirlo, luego la restricción que se debe cumplir para poder recuperar la palabra original es:
      <SafeMJ scrollable>{"\\[2e+b<d,\\]"}</SafeMJ>
      donde <SafeMJ>{"\\(b\\)"}</SafeMJ> es la cantidad de borrados. Te dejo como ejercicio la demostración,
      que es simplemente generalizar la desigualdad triangular que vimos antes, teniendo en cuenta que en las posiciones borradas no se puede comparar.
      Concluimos, por tanto, que en un código lineal MDS, como es el caso de Reed-Solomon, se puede corregir cualquier combinación de errores y borrados
      que cumpla:
      <SafeMJ scrollable>{"\\[2e+b\\le n-k.\\tag{4.3}\\]"}</SafeMJ>
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">5. Decodificación con errores</h2>

    <p className="mb-3">
      Oye, ¿a qué viene esta sección? ¿No hemos dicho que con la búsqueda de <SafeMJ>{"\\((4.1)\\)"}</SafeMJ> podemos recuperarnos de los
      errores? Ah, bueno, espera, es que esa fuerza bruta está muy bien teóricamente, pero en la práctica es intratable. Date cuenta que calcular cada
      distancia consta de <SafeMJ>{"\\(n\\)"}</SafeMJ> comparaciones de componentes, y como hay <SafeMJ>{"\\(q^k\\)"}</SafeMJ> palabras en el código,
      el coste total se va a <SafeMJ after=".">{"\\(O(nq^k)\\)"}</SafeMJ> ¡Y eso es exponencial en <SafeMJ after="!">{"\\(k\\)"}</SafeMJ>
    </p>

    <p className="mb-3">
      Tenemos, por tanto, que encontrar un algoritmo que sí sea asequible para esta tarea. Para ello, pongámonos en la situación de antes y supongamos que recibimos una palabra <SafeMJ>{"\\(r\\in\\mathbb{F}_q^n\\)"}</SafeMJ> con
      una cantidad de errores <SafeMJ>{"\\(e\\)"}</SafeMJ> no mayor a la capacidad correctora de Reed-Solomon, es decir, satisfaciendo <SafeMJ after=".">{"\\(e\\le t=\\lfloor(n-k)/2\\rfloor\\)"}</SafeMJ>
    </p>

    <p className="mb-3">
      Entonces, sea <SafeMJ>{"\\(m(x)\\in\\mathbb{F}_q[x]_{<k}\\)"}</SafeMJ> el polinomio del mensaje original e <SafeMJ>{"\\(I:=\\{i:m(\\alpha_i)\\neq r_i\\}\\)"}</SafeMJ> el conjunto de posiciones erróneas, definimos el <b>polinomio localizador de errores</b> como:
      <SafeMJ scrollable>{"\\[E^{\\star}(x):=\\prod_{i\\in I}(x-\\alpha_i),\\]"}</SafeMJ>
      el cual es mónico y tiene grado <SafeMJ after=".">{"\\(e\\)"}</SafeMJ> Su utilidad es que se anula, precisamente, en los <SafeMJ after="'s">{"\\(\\alpha_i\\)"}</SafeMJ> correspondientes
      a coordenadas con errores, lo cual hace que, para todo <SafeMJ after=",">{"\\(i\\in\\{1,\\dots,n\\}\\)"}</SafeMJ> se cumpla la siguiente identidad clave:
      <SafeMJ scrollable>{"\\[m(\\alpha_i)\\,E^{\\star}(\\alpha_i)=r_i\\,E^{\\star}(\\alpha_i).\\tag{5.1}\\]"}</SafeMJ>
      Efectivamente, si <SafeMJ after=",">{"\\(i\\in I\\)"}</SafeMJ> entonces ambos lados son <SafeMJ after=".">{"\\(0\\)"}</SafeMJ> En este caso, <SafeMJ>{"\\(E^\\star(x)\\)"}</SafeMJ> "apaga", por así decirlo,
      la discrepancia entre <SafeMJ>{"\\(m(\\alpha_i)\\)"}</SafeMJ> y <SafeMJ after=".">{"\\(r_i\\)"}</SafeMJ> Si, por el contrario, <SafeMJ after=",">{"\\(i\\not\\in I\\)"}</SafeMJ>{" "}
      es que entonces no hay error en la <SafeMJ after="ésima">{"\\(i\\text{-}\\)"}</SafeMJ> posición y <SafeMJ>{"\\(m(\\alpha_i)\\)"}</SafeMJ> coincide con <SafeMJ after=".">{"\\(r_i\\)"}</SafeMJ>
    </p>

    <p className="mb-3">
      Ahora, fíjate que la parte izquierda de la igualdad anterior sigue involucrando a <SafeMJ after=",">{"\\(m(x)\\)"}</SafeMJ> que es nuestra incógnita a resolver,
      luego no es ninguna locura absorberla en el siguiente polinomio: <SafeMJ after=".">{"\\(Q^{\\star}(x):=m(x)\\,E^{\\star}(x)\\)"}</SafeMJ>{" "}
      Así, como <SafeMJ after=",">{"\\(\\mathrm{gr}(m)<k\\)"}</SafeMJ> entonces <SafeMJ after=",">{"\\(\\mathrm{gr}(Q^{\\star})<k+e\\)"}</SafeMJ> pero 
      sobre todo y más importante, la identidad anterior se transforma entonces en:
      <SafeMJ scrollable>{"\\[Q^{\\star}(\\alpha_i)=r_i\\,E^{\\star}(\\alpha_i),\\quad\\forall{i=1,\\ldots,n}.\\tag{5.2}\\]"}</SafeMJ>
      Y esto... es mucho más potente de lo que parece, pues escribiendo los coeficientes de los polinomios:
      <SafeMJ scrollable>{"\\[Q^{\\star}(x)=\\sum_{j=0}^{k+e-1}q_jx^j,\\quad E^{\\star}(x)=x^{e}+\\sum_{j=0}^{e-1}e_jx^j,\\]"}</SafeMJ>
      la expresión <SafeMJ>{"\\((5.2)\\)"}</SafeMJ> se transforma en:
      <SafeMJ scrollable>{"\\[\\sum_{j=0}^{k+e-1}q_j\\alpha_i^j-r_i\\sum_{j=0}^{e-1}e_j\\alpha_i^j=r_i\\alpha_i^e,\\quad\\forall{i=1,\\ldots,n},\\tag{5.3}\\]"}</SafeMJ>
      que no es más que un sistema lineal de <SafeMJ>{"\\(n\\)"}</SafeMJ> ecuaciones con <SafeMJ>{"\\(2e+k\\)"}</SafeMJ> incógnitas (<SafeMJ>{"\\(k+e\\)"}</SafeMJ> <SafeMJ after="'s">{"\\(q_j\\)"}</SafeMJ>{" "}
      y <SafeMJ>{"\\(e\\)"}</SafeMJ> <SafeMJ after="'s),">{"\\(e_j\\)"}</SafeMJ> el cual sabemos que tiene solución <SafeMJ>{"\\((E^\\star(x), Q^\\star(x))\\)"}</SafeMJ> por construcción.
    </p>

    <p className="mb-3">
      ¡Ah, genial! Entonces está claro: resolvemos el sistema, hallamos <SafeMJ>{"\\(Q^{\\star}(x)\\)"}</SafeMJ> y <SafeMJ>{"\\(E^{\\star}(x)\\)"}</SafeMJ> y recuperamos <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> simplemente
      dividiendo el primer polinomio entre el segundo.
      ¡Qué fácil! Bueno, no tan rápido, vaquero... La idea es más o menos esa, pero hay dos detalles de los que no hemos hablado y sin
      los cuales el algoritmo anterior podría estrellarse:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"> El primero. Fíjate que sabes que <SafeMJ>{"\\((5.3)\\)"}</SafeMJ> tiene una solución, pero no sabes si tiene más. Podrías plantear
        el sistema y acabar con infinitas soluciones entre las que elegir.</li>
        <li className="mb-1">Y segundo, y aún peor, desde un punto de vista práctico, ¡ni siquiera puedes plantear el sistema anterior! ¿Que por qué?
        Pues porque el receptor no tiene ni idea de la cantidad <SafeMJ after=".">{"\\(e\\)"}</SafeMJ> En un proceso de transmisión real, este solo recibe la palabra <SafeMJ after=",">{"\\(r\\)"}</SafeMJ>{" "}
        que a saber cuántos errores ha sufrido por el camino. Pff, es cierto...
        </li>
      </ul>
      Bueno, tranquilo. No suspires, que por suerte la siguiente proposición nos va a resolver la vida:
    </p>

    <p className="mb-3">
      <b>Proposición 5.4.</b> Para cualquier par de polinomios <SafeMJ>{"\\(Q(x),E(x)\\in\\mathbb{F}_q[x]\\)"}</SafeMJ> satisfaciendo:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"><SafeMJ>{"\\(E(x)\\)"}</SafeMJ> es mónico con <SafeMJ after=",">{"\\(\\mathrm{gr}(E)\\le t\\)"}</SafeMJ></li>
        <li className="mb-1"><SafeMJ after=",">{"\\(\\mathrm{gr}(Q)< t+k\\)"}</SafeMJ></li>
        <li className="mb-1"><SafeMJ after=",">{"\\(Q(\\alpha_i)=r_i\\,E(\\alpha_i),\\,\\forall i=1,\\dots,n\\)"}</SafeMJ></li>
      </ul>
      se cumple: <SafeMJ scrollable>{"\\[Q(x)=m(x)\\,E(x).\\]"}</SafeMJ> En particular, si <SafeMJ after=",">{"\\(\\mathrm{gr}(E)\\le e\\)"}</SafeMJ>{" "}
      entonces: <SafeMJ scrollable>{"\\[E(x)=E^\\star(x),\\quad Q(x)=Q^\\star(x).\\]"}</SafeMJ>
    </p>

    <Proof>
    <p className="mb-3">
      <em>Demostración.</em> Sean <SafeMJ>{"\\(Q(x),E(x)\\in\\mathbb{F}_q[x]\\)"}</SafeMJ> satisfaciendo las condiciones iniciales del enunciado,
      consideramos el siguiente polinomio:
      <SafeMJ scrollable>{"\\[\\Delta(x)=Q(x)\\,E^\\star(x)-Q^\\star(x)\\,E(x),\\]"}</SafeMJ>
      el cual cumple, para todo <SafeMJ after=":">{"\\(i=1,\\ldots,n\\)"}</SafeMJ>
      <SafeMJ scrollable>{`
        \\[
        \\begin{aligned}
        \\Delta(\\alpha_i)
        &= Q(\\alpha_i)\\,E^\\star(\\alpha_i)-Q^\\star(\\alpha_i)\\,E(\\alpha_i) \\\\[5pt]
        &= r_i\\,E(\\alpha_i)\\,E^\\star(\\alpha_i)-r_i\\,E^\\star(\\alpha_i)\\,E(\\alpha_i) = 0.
        \\end{aligned}
        \\]
      `}
      </SafeMJ>
      Entonces, como los <SafeMJ>{"\\(n\\)"}</SafeMJ> <SafeMJ after="'s">{"\\(\\alpha_i\\)"}</SafeMJ> son distintos
      y <SafeMJ after=",">{"\\(\\mathrm{gr}(\\Delta)<t+k+e\\le 2t+k\\le n\\)"}</SafeMJ> usando
      (como ya es costumbre) el lema <SafeMJ after=",">{"\\((2.2)\\)"}</SafeMJ> se tiene que <SafeMJ after=".">{"\\(\\Delta(x)\\equiv 0\\)"}</SafeMJ>{" "}
      Y como <SafeMJ>{"\\(E^{\\star}(x)\\not\\equiv 0\\)"}</SafeMJ> en el cuerpo <SafeMJ after=",">{"\\(\\mathbb{F}_q[x]\\)"}</SafeMJ>{" "}
      podemos pasar dividiendo para obtener:
      <SafeMJ scrollable>{"\\[Q(x)=\\frac{Q^\\star(x)}{E^\\star(x)}\\,E(x)=m(x)\\,E(x).\\]"}</SafeMJ>
      Así, para todo <SafeMJ after=",">{"\\(i\\in\\{1,\\ldots,n\\}\\)"}</SafeMJ> nos quedan las siguientes igualdades:
      <SafeMJ scrollable>{"\\[r_i\\,E(\\alpha_i)=Q(\\alpha_i)=m(\\alpha_i)\\,E(\\alpha_i),\\]"}</SafeMJ>
      de forma que si <SafeMJ after=",">{"\\(i\\in I\\)"}</SafeMJ> <SafeMJ>{"\\(m(\\alpha_i)\\neq r_i\\)"}</SafeMJ> y entonces necesariamente <SafeMJ>{"\\(E(\\alpha_i)=0\\)"}</SafeMJ> (si no, podríamos dividir ambos lados
      por <SafeMJ after=",">{"\\(E(\\alpha_i)\\neq 0\\)"}</SafeMJ> llegando a <SafeMJ after=").">{"\\(m(\\alpha_i)=r_i\\,!!!\\)"}</SafeMJ>{" "}
      Es decir, que las <SafeMJ>{"\\(e\\)"}</SafeMJ> raíces de <SafeMJ>{"\\(E^\\star(x)\\)"}</SafeMJ> son también raíces de <SafeMJ after=".">{"\\(E(x)\\)"}</SafeMJ>{" "}
      Luego en el caso particular de que <SafeMJ after=",">{"\\(\\mathrm{gr}(E)\\le e\\)"}</SafeMJ> y al ser ambos polinomios mónicos,
      se tiene que <SafeMJ>{"\\(E(x)=E^\\star(x)\\)"}</SafeMJ> y por ende que <SafeMJ after=".">{"\\(Q(x)=Q^\\star(x)\\)"}</SafeMJ>
      <span className="qed"><SafeMJ>{"\\(\\square\\)"}</SafeMJ></span>
    </p>
    </Proof>

    <p className="mb-3">
      Buah, esto nos aclara todas las dudas. Ahora sabemos que el sistema que planteamos al inicio tenía, efectivamente, solución única <SafeMJ after=".">{"\\((Q^\\star(x),E^\\star(x))\\)"}</SafeMJ>{" "}
      Pero es que además, no nos importa no saber la cantidad de errores que han sucedido. <b>Podemos ponernos en la peor situación (<SafeMJ after="),">{"\\(\\boldsymbol{e=t}\\)"}</SafeMJ> resolver
      ese sistema "ampliado" y siempre obtendremos una solución <SafeMJ>{"\\(\\boldsymbol{(Q(x),E(x))}\\)"}</SafeMJ> que, a efectos de recuperar el mensaje original, nos sigue sirviendo</b>, pues cumplirá:
      <SafeMJ scrollable>{"\\[m(x)=\\frac{Q(x)}{E(x)}.\\]"}</SafeMJ>
      Eso sí, en este segundo caso, los polinomios obtenidos no serán los de grado mínimo, como sí lo eran <SafeMJ>{"\\(Q^\\star(x)\\)"}</SafeMJ> y <SafeMJ after=".">{"\\(E^\\star(x)\\)"}</SafeMJ>{" "}
      Vendrán multiplicados por un polinomio mónico de grado <SafeMJ after=",">{"\\(t-e\\)"}</SafeMJ> pero bueno, al dividir se cancelan y listo.
    </p>

    <p className="mb-3">
      Hemos conseguido, por tanto, definir correctamente un algoritmo para la tarea de decodificación que buscábamos, el cual es conocido
      en la literatura como <b>algoritmo de Berlekamp-Welch</b>. Otra palabreja más, sí, aunque lo importante es que hayas entendido el sencillo,
      y a la vez potente, juego de polinomios que hace que funcione.
    </p>

    <p className="mb-3">
      Veámoslo en acción, pues. Tomemos de nuevo nuestro ejemplo de referencia de <SafeMJ after=",">{"\\(\\textrm{`}ouo\\textrm{'}\\)"}</SafeMJ>{" "}
      donde dijimos que <SafeMJ after=",">{"\\(n=q=5\\)"}</SafeMJ> <SafeMJ>{"\\(k=3\\)"}</SafeMJ> y <SafeMJ after=",">{"\\(\\alpha_i=i-1\\)"}</SafeMJ> para <SafeMJ after=",">{"\\(i=1,2,3,4,5\\)"}</SafeMJ>{" "}
      y supongamos que se da el siguiente error de transmisión:
      <SafeMJ scrollable>{"\\[m=(3,4,3)\\longrightarrow c=(3,0,3,2,2)\\longrightarrow r=(3,0,2,2,2).\\]"}</SafeMJ>
      Como en este caso <SafeMJ after=",">{"\\(t=1\\)"}</SafeMJ> buscamos dos polinomios en <SafeMJ after=":">{"\\(\\mathbb{F}_5[x]\\)"}</SafeMJ>
      <SafeMJ scrollable>{"\\[Q(x)=q_3x^3+q_2x^2+q_1x+q_0,\\quad E(x)=x+e_0,\\]"}</SafeMJ>
      tales que:
      <SafeMJ scrollable>{`
        \\[
        \\begin{cases}
        Q(0)=3\\,E(0) & \\\\
        Q(1)=0 & \\\\
        Q(2)=2\\,E(2) & \\\\
        Q(3)=2\\,E(3) & \\\\
        Q(4)=2\\,E(4)
        \\end{cases}
        \\longrightarrow
        \\begin{cases}
        q_0+2e_0=0 & \\\\
        q_3+q_2+q_1+q_0=0 & \\\\
        3q_3+4q_2+2q_1+q_0+3e_0=4 & \\\\
        2q_3+4q_2+3q_1+q_0+3e_0=1 & \\\\
        4q_3+q_2+4q_1+q_0+3e_0=3
        \\end{cases}
        \\]
      `}</SafeMJ>
      Resolviendo por Gauss-Jordan (te dejo completarlo), se obtiene:
      <SafeMJ scrollable>{`
        \\[
        \\left[
          \\begin{array}{ccccc|c}
            0&0&0&1&2&0\\\\ 1&1&1&1&0&0\\\\ 3&4&2&1&3&4\\\\ 2&4&3&1&3&1\\\\ 4&1&4&1&3&3
          \\end{array}
        \\right]
        \\longrightarrow
        \\left[
          \\begin{array}{ccccc|c}
            1&1&1&1&0&0\\\\ 0&1&4&3&3&4\\\\ 0&2&1&4&3&1\\\\ 0&0&0&1&2&0\\\\ 0&3&3&4&2&1
          \\end{array}
        \\right]
        \\longrightarrow
        \\left[
          \\begin{array}{ccccc|c}
            1&0&2&3&2&1\\\\ 0&1&4&3&3&4\\\\ 0&0&3&3&2&3\\\\ 0&0&0&1&2&0\\\\ 0&0&1&0&3&4
          \\end{array}
        \\right]
        \\]
      `}</SafeMJ>
      <SafeMJ scrollable>{`
        \\[
        \\longrightarrow
        \\left[
          \\begin{array}{ccccc|c}
            1&0&0&3&1&3\\\\ 0&1&0&3&1&3\\\\ 0&0&1&0&3&4\\\\ 0&0&0&1&2&0\\\\ 0&0&0&3&3&1
          \\end{array}
        \\right]
        \\longrightarrow
        \\cdots
        \\longrightarrow
        \\left[
          \\begin{array}{ccccc|c}
            1&0&0&0&0&3\\\\ 0&1&0&0&0&3\\\\ 0&0&1&0&0&0\\\\ 0&0&0&1&0&4\\\\ 0&0&0&0&1&3
          \\end{array}
        \\right]
        \\]
      `}</SafeMJ>
      Luego los polinomios solución resultan ser:
      <SafeMJ scrollable>{"\\[Q(x)=3x^3+3x^2+4,\\quad E(x)=x+3,\\]"}</SafeMJ>
      y recuperamos el mensaje original dividiendo:
      <SafeMJ scrollable>{"\\[m(x)=\\frac{Q(x)}{E(x)}=\\frac{3x^3+3x^2+4}{x+3}=3x^2+4x+3.\\]"}</SafeMJ>
      Chulo, y sobre todo fácil, ¿no? Además, fíjate que en este caso se tiene <SafeMJ after=",">{"\\(e=t\\)"}</SafeMJ> luego los polinomios hallados son en
      realidad <SafeMJ>{"\\(Q^\\star(x)\\)"}</SafeMJ> y <SafeMJ after=",">{"\\(E^\\star(x)\\)"}</SafeMJ> y por tanto, sabemos que la raíz <SafeMJ>{"\\(\\alpha_2=2\\)"}</SafeMJ> de <SafeMJ>{"\\(E(x)\\)"}</SafeMJ> nos
      indica que el único error sucedió en la tercera coordenada de la palabra. ¡Bingo!
    </p>

    <p className="mb-3">
      Si, por el contrario, la palabra no hubiese sufrido ningún error de transmisión, es decir, que <SafeMJ after=",">{"\\(0=e<t=1\\)"}</SafeMJ> te invito a comprobar que el sistema correspondiente
      da las siguientes soluciones:
      <SafeMJ scrollable>{"\\[Q(x)=3x^3+x^2+4x+2,\\quad E(x)=x+4.\\]"}</SafeMJ>
      Evidentemente podemos seguir recuperando <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> dividiendo <SafeMJ>{"\\(Q(x)\\)"}</SafeMJ> entre <SafeMJ after=",">{"\\(E(x)\\)"}</SafeMJ>{" "}
      lo único que ahora <SafeMJ>{"\\(E(x)\\)"}</SafeMJ> no coincide con el polinomio localizador de errores y, de hecho, incluye una <em>raíz espuria</em> (falsa): <SafeMJ after=".">{"\\(\\alpha_1=1\\)"}</SafeMJ>
    </p>

    <p className="mb-3">
      Y ya, por acabar de discutir todos los casos. ¿Qué hubiera pasado si <SafeMJ after="?">{"\\(e>t\\)"}</SafeMJ> En primer lugar, fíjate que entonces <SafeMJ>{"\\(E^\\star(x)\\)"}</SafeMJ> tendría
      grado mayor que <SafeMJ after=",">{"\\(t\\)"}</SafeMJ> luego ya no estaría garantizado que el sistema que resuelve Berlekamp-Welch tuviera solución. Incluso si la tuviera,
      la desigualdad <SafeMJ>{"\\(e\\le t\\)"}</SafeMJ> fue imprescindible en la demostración de la proposición <SafeMJ after=",">{"\\((5.4)\\)"}</SafeMJ> tanto para demostrar
      que <SafeMJ>{"\\(E(x)\\)"}</SafeMJ> divide a <SafeMJ>{"\\(Q(x)\\)"}</SafeMJ> como para que dicho cociente coincida con el polinomio del mensaje original. Sin esa condición,
      por tanto, cualquiera de estas tres casuísticas podría chafarnos la decodificación. Por ejemplo, te dejo como ejercicio que estudies el caso
      de haber recibido la palabra <SafeMJ after=",">{"\\(r=(4,0,2,2,2)\\)"}</SafeMJ> la cual ya contiene dos errores.
    </p>

    <p className="mb-3">
      Por último. ¿Y si la palabra recibida, como mencionamos al final de la sección anterior, también contuviera <SafeMJ>{"\\(b\\)"}</SafeMJ> borrados? Pues siempre que se siguiera cumpliendo la
      desigualdad <SafeMJ after=",">{"\\((4.3)\\)"}</SafeMJ> Berlekamp-Welch seguiría funcionando. Simplemente descartaríamos las <SafeMJ>{"\\(b\\)"}</SafeMJ> posiciones borradas
      y plantearíamos el sistema con las <SafeMJ>{"\\(n-b\\)"}</SafeMJ> ecuaciones restantes. En otras palabras, sería como el caso estándar,
      solo que con una capacidad correctora de <SafeMJ>{"\\(t'=\\lfloor(n-b-k)/2\\rfloor\\)"}</SafeMJ> errores.
    </p>

    <p className="mb-3">
      Respecto a la complejidad computacional de Berlekamp-Welch, fíjate que el algoritmo solo consta de:
      <ul className="list-decimal list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1 marker:font-bold">Resolver un sistema lineal con <SafeMJ>{"\\(n\\)"}</SafeMJ> ecuaciones y <SafeMJ>{"\\(2t+k\\le n\\)"}</SafeMJ> incógnitas,
        lo cual tiene un coste <SafeMJ>{"\\(O(n^3)\\)"}</SafeMJ> en general, como ya sabes.
        </li>
        <li className="mb-1 marker:font-bold">Dividir un polinomio de grado <SafeMJ>{"\\(t+k-1\\)"}</SafeMJ> entre otro de grado <SafeMJ after=".">{"\\(t\\)"}</SafeMJ> Como para
        cada uno de los <SafeMJ>{"\\(k\\)"}</SafeMJ> coeficientes del polinomio cociente hay que hacer <SafeMJ>{"\\(t+1\\)"}</SafeMJ> multiplicaciones y sumas, el número de
        operaciones será <SafeMJ after=",">{"\\(O(k(t+1))=O(n^2)\\)"}</SafeMJ> lo cual es despreciable respecto al coste anterior.
        </li>
      </ul>
      Vamos, que la complejidad total es de <SafeMJ after=".">{"\\(O(n^3)\\)"}</SafeMJ> No especialmente buena, la verdad, pero al menos
      es razonable comparada con la fuerza bruta que expusimos al principio. Y además, no podrás negarme que el algoritmo es bastante elegante, ¿eh? 
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">6. Reed-Solomon en la práctica</h2>

    <p className="mb-3">
      Hasta ahora hemos descrito Reed-Solomon tal y como se presentó en el <em>paper</em> original, es decir, desde el punto de vista de la evaluación: un mensaje es un polinomio y la palabra del 
      código se obtiene evaluándolo en distintos elementos del cuerpo. Esta construcción es especialmente cómoda para entender matemáticamente el código y construir algoritmos elementales como Berlekamp-Welch.
      Sin embargo, para implementar ciertas configuraciones de Reed-Solomon en dispositivos reales, existe otra forma más útil de describir el código.
    </p>

    <p className="mb-3">
      Para ello, recuperemos nuestra querida Teoría de Galois y consideremos un elemento primitivo <SafeMJ>{"\\(\\alpha\\)"}</SafeMJ> del cuerpo finito <SafeMJ after=".">{"\\(\\mathbb{F}_q\\)"}</SafeMJ>{" "}
      Entonces, si tomamos como puntos de evaluación <SafeMJ after=",">{"\\(1,\\alpha,\\dots,\\alpha^{n-1}\\)"}</SafeMJ> el código Reed-Solomon clásico nos queda:
      <SafeMJ scrollable>{"\\[\\mathcal{C_{\\mathrm{eval}}}:=\\left\\{(m(1),m(\\alpha),\\dots,m(\\alpha^{n-1}))\\in\\mathbb{F}_q^n:m(x)\\in\\mathbb{F}_q[x]_{<k}\\right\\}.\\]"}</SafeMJ>
      Es precisamente esta idea de tomar como puntos de evaluación potencias consecutivas de un elemento primitivo la que nos permitirá definir una nueva forma de construir Reed-Solomon.
      Además, para garantizar que estos puntos de evaluación sean distintos, impondremos <SafeMJ after=",">{"\\(n\\le q-1\\)"}</SafeMJ> pues
      no olvidemos que <SafeMJ after=",">{"\\(\\alpha^{q-1}=1\\)"}</SafeMJ> al tener <SafeMJ>{"\\(\\alpha\\)"}</SafeMJ> orden <SafeMJ after=".">{"\\(q-1\\)"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      Dicho esto, pasamos a definir el <b>polinomio generador</b> asociado a esta configuración como:
      <SafeMJ scrollable>{"\\[g(x):=\\prod_{i=1}^{n-k}(x-\\alpha^i)\\in\\mathbb{F}_q[x],\\]"}</SafeMJ>
      el cual vemos tiene grado <SafeMJ>{"\\(n-k\\)"}</SafeMJ> y es mónico.
    </p>

    <p className="mb-3">
      Entonces, el <b>código generado por <SafeMJ>{"\\(\\boldsymbol{g(x)}\\)"}</SafeMJ></b> se define como:
      <SafeMJ scrollable>{"\\[\\mathcal{C}_g:=\\left\\{(c_0,\\dots,c_{n-1})\\in\\mathbb{F}_q^n:g(x)\\mid c(x)=\\sum_{i=0}^{n-1} c_i x^i\\right\\},\\]"}</SafeMJ>
      es decir, como los vectores de coeficientes de todos los polinomios de <SafeMJ>{"\\(\\mathbb{F}_q[x]\\)"}</SafeMJ> de grado menor que <SafeMJ>{"\\(n\\)"}</SafeMJ> que son múltiplos de <SafeMJ after=".">{"\\(g(x)\\)"}</SafeMJ>
    </p>
    
    <p className="mb-3">
      Repitiendo un argumento similar al que hicimos con la versión clásica de Reed-Solomon, es fácil comprobar que la aplicación:
      <SafeMJ scrollable>{"\\[\\psi:\\mathbb{F}_q[x]_{<k}\\longrightarrow\\mathbb{F}_q^n,\\quad m(x)\\longmapsto \\textrm{coeff}(m(x)g(x))\\]"}</SafeMJ>
      es lineal e inyectiva, luego <SafeMJ>{"\\(\\mathcal{C}_g\\)"}</SafeMJ> es un código lineal de dimensión <SafeMJ>{"\\(k\\)"}</SafeMJ> y longitud <SafeMJ after=".">{"\\(n\\)"}</SafeMJ>{" "}
      De hecho, si escribimos:
      <SafeMJ scrollable>{"\\[g(x)=g_0+g_1x+\\cdots+g_{n-k-1}x^{n-k-1}+x^{n-k},\\]"}</SafeMJ>
      su matriz generadora resulta ser:
      <SafeMJ scrollable>{`\\[G=
      \\begin{pmatrix}
      g_0 & g_1 & \\cdots & g_{n-k-1} & 1 & 0 & \\cdots & 0\\\\
      0 & g_0 & \\cdots & g_{n-k-2} & g_{n-k-1} & 1 & \\cdots & 0\\\\
      \\vdots & \\vdots & \\ddots & \\ddots & \\ddots & \\ddots & \\ddots & \\vdots\\\\
      0 & 0 & \\cdots & g_0 & g_1 & \\cdots & g_{n-k-1} & 1
      \\end{pmatrix}.
      \\]`}</SafeMJ>
      pues sus filas son precisamente los vectores de coeficientes de <SafeMJ after=",">{"\\(g(x),xg(x),\\dots,x^{k-1}g(x)\\)"}</SafeMJ> respectivamente.
    </p>

    <p className="mb-3">
      Vale, hemos conseguido codificar el polinomio <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> de una
      nueva forma, ¿pero qué tiene que ver esto con nuestro Reed-Solomon? En apariencia, <SafeMJ>{"\\(\\mathcal{C}_g\\)"}</SafeMJ> parece un código
      completamente distinto al que hemos manejado hasta ahora, ¿no? Pues nada de eso. Pero antes de demostrarlo,
      necesitaremos una pequeña identidad.
    </p>

    <p className="mb-3">
      <b>Lema 6.1.</b> Sea <SafeMJ>{"\\(\\alpha\\)"}</SafeMJ> un elemento de orden <SafeMJ>{"\\(n\\)"}</SafeMJ> en <SafeMJ after=".">{"\\(\\mathbb{F}_q\\)"}</SafeMJ>{" "}
      Entonces, para cualquier entero <SafeMJ after=",">{"\\(t\\)"}</SafeMJ> se tiene:
      <SafeMJ scrollable>{"\\[\\sum_{j=0}^{n-1}\\alpha^{tj}=\\begin{cases}n,&n\\mid t,\\\\0,&n\\nmid t.\\end{cases}\\]"}</SafeMJ>
    </p>

    <Proof>
      <p className="mb-3">
        <em>Demostración.</em> Si <SafeMJ after=",">{"\\(n\\mid t\\)"}</SafeMJ> entonces <SafeMJ>{"\\(\\alpha^t=1\\)"}</SafeMJ> y, por
        tanto, <SafeMJ>{"\\(\\sum_{j=0}^{n-1}\\alpha^{tj}=\\sum_{j=0}^{n-1}1=n.\\)"}</SafeMJ>
      </p>

      <p className="mb-3">
        Si, por el contrario, <SafeMJ after=",">{"\\(n\\nmid t\\)"}</SafeMJ> entonces <SafeMJ>{"\\(\\alpha^t\\neq1\\)"}</SafeMJ> y
        podemos aplicar la fórmula de la suma geométrica para obtener:
        <SafeMJ scrollable>{"\\[\\sum_{j=0}^{n-1}\\alpha^{tj}=\\frac{(\\alpha^{t})^n-1}{\\alpha^t-1}=\\frac{1-1}{\\alpha^t-1}=0.\\tag*{$\\square$}\\]"}</SafeMJ>
      </p>
    </Proof>

    <p className="mb-3">
      Con este resultado auxiliar, ya podemos pasar a probar que ambas construcciones tenían, en realidad, muchísimo que ver.
    </p>

    <p className="mb-3">
      <b>Proposición 6.2.</b> Si <SafeMJ after=",">{"\\(n=q-1\\)"}</SafeMJ> entonces <SafeMJ after=".">{"\\(\\mathcal{C}_{\\mathrm{eval}}=\\mathcal{C}_g\\)"}</SafeMJ>
    </p>

    <Proof>
      <p className="mb-3">
        <em>Demostración.</em> Como ambos códigos son lineales y de la misma dimensión, basta con demostrar
        que uno de ellos está contenido en el otro. En particular, veamos
        que <SafeMJ after=".">{"\\(\\mathcal{C}_{\\mathrm{eval}}\\subseteq\\mathcal{C}_g\\)"}</SafeMJ>
      </p>

      <p className="mb-3">
        El caso <SafeMJ>{"\\(n=k\\)"}</SafeMJ> es trivial, pues entonces <SafeMJ>{"\\(g(x)=1\\)"}</SafeMJ> y <SafeMJ after=".">{"\\(\\mathcal{C}_{\\mathrm{eval}}=\\mathcal{C}_g=\\mathbb{F}_q^n\\)"}</SafeMJ>{" "}
        Luego supondremos <SafeMJ after=".">{"\\(n>k\\)"}</SafeMJ>
      </p>

      <p className="mb-3">
        Entonces, sea <SafeMJ after=",">{"\\(c=(c_0,\\dots,c_{n-1})\\in\\mathcal{C}_{\\mathrm{eval}}\\)"}</SafeMJ> por
        definición, existe un polinomio <SafeMJ after=",">{"\\(m(x)=\\sum_{i=0}^{k-1}m_i x^i\\)"}</SafeMJ> tal que:
        <SafeMJ scrollable>{"\\[m(\\alpha^j)=c_j,\\quad \\forall j=0,\\dots,n-1.\\]"}</SafeMJ>
      </p>

      <p className="mb-3">
        Como <SafeMJ>{"\\(c\\in\\mathcal{C}_g\\)"}</SafeMJ> si y solo si el polinomio <SafeMJ>{"\\(c(x):=\\sum_{j=0}^{n-1}c_jx^j\\)"}</SafeMJ> es divisible por <SafeMJ after=",">{"\\(g(x)\\)"}</SafeMJ> y este
        último tiene a <SafeMJ>{"\\(\\alpha,\\alpha^2,\\dots,\\alpha^{n-k}\\)"}</SafeMJ> como únicas raíces, basta con ver que:
        <SafeMJ scrollable>{"\\[c(\\alpha^s)=0,\\quad\\forall s=1,\\dots,n-k.\\]"}</SafeMJ>
      </p>

      <p className="mb-3">
        En efecto, para cualquiera de esos valores de <SafeMJ after=":">{"\\(s\\)"}</SafeMJ>
        <SafeMJ scrollable>{`
          \\[\\begin{aligned}c(\\alpha^s)&=\\sum_{j=0}^{n-1}c_j\\alpha^{sj}=\\sum_{j=0}^{n-1}m(\\alpha^j)\\alpha^{sj}\\\\
          &=\\sum_{j=0}^{n-1}\\left(\\sum_{i=0}^{k-1}m_i\\alpha^{ij}\\right)\\alpha^{sj}\\\\
          &=\\sum_{i=0}^{k-1}m_i\\sum_{j=0}^{n-1}\\alpha^{(i+s)j}.\\end{aligned}\\]
        `}</SafeMJ>
      </p>

      <p className="mb-3">
        Como <SafeMJ after=",">{"\\(1\\leq i+s\\leq n-1\\)"}</SafeMJ> entonces <SafeMJ after=".">{"\\(n\\nmid(i+s)\\)"}</SafeMJ> Por tanto, como <SafeMJ>{"\\(\\alpha\\)"}</SafeMJ> tiene
        orden <SafeMJ after=",">{"\\(n=q-1\\)"}</SafeMJ> podemos usar el lema <SafeMJ>{"\\((6.1)\\)"}</SafeMJ> para concluir que:
        <SafeMJ scrollable>{"\\[\\sum_{j=0}^{n-1}\\alpha^{(i+s)j}=0.\\tag*{$\\square$}\\]"}</SafeMJ>
      </p>
    </Proof>

    <p className="mb-3">
      En definitiva, resulta que ambas construcciones describen exactamente el mismo código. Fuerte. Eso sí, no necesariamente establecen
      la misma correspondencia entre mensajes y palabras del código. Es decir, un mismo mensaje puede dar lugar a palabras
      distintas según el método utilizado, solo que al recorrer todos los mensajes posibles, entonces el conjunto de palabras resultantes
      sí que será el mismo.
    </p>

    <p className="mb-3">
      Respecto a la hipótesis de la proposición, que ya sé que más de uno lo estará pensando, es cierto que el caso que hemos demostrado
      corresponde a una elección de <SafeMJ after=",">{"\\(n\\)"}</SafeMJ> de los puntos de evaluación y del polinomio generador, particularmente conveniente.
      Todo ha cuadrado perfectamente, la verdad.
    </p>

    <p className="mb-3">
      En otras configuraciones habituales, por ejemplo con <SafeMJ after=",">{"\\(n<q-1\\)"}</SafeMJ> con el índice de <SafeMJ>{"\\(g(x)\\)"}</SafeMJ> recorriendo <SafeMJ>{"\\(i=0,\\ldots,n-k-1\\)"}</SafeMJ> o
      escogiendo los puntos de evaluación en otro orden,
      la igualdad anterior, lamentablemente, deja de ser cierta en general. Sin embargo, y he aquí la clave, en estas variantes <SafeMJ>{"\\(\\mathcal{C}_g\\)"}</SafeMJ> es
      lo que se llama <b>monomialmente equivalente</b> al Reed-Solomon de evaluación.
      Esto es, podemos transformar un código en el otro simplemente reordenando y/o escalando adecuadamente las coordenadas de las palabras,
      lo cual requiere adaptar un poco la demostración anterior con factores y permutaciones adicionales.
      Si lo piensas, eso implica que ambos códigos son "en esencia" el mismo, pues la distancia de Hamming entre palabras se conserva.
    </p>

    <p className="mb-3">
      Genial, pues entonces ya tenemos una nueva forma de expresar Reed-Solomon. Pero ¿por qué esta variante es mejor para la implementación?
      Para verlo, hagamos una pequeña modificación al método de codificación. En lugar de multiplicar un  mensaje <SafeMJ>{"\\(m(x)\\)"}</SafeMJ> por <SafeMJ after=",">{"\\(g(x)\\)"}</SafeMJ>{" "}
      consideramos el polinomio <SafeMJ>{"\\(m(x)x^{n-k}\\)"}</SafeMJ> y lo dividimos entre <SafeMJ after=":">{"\\(g(x)\\)"}</SafeMJ>
      <SafeMJ scrollable>{"\\[m(x)x^{n-k}=q(x)g(x)+s(x),\\quad\\textnormal{con }\\mathrm{gr}(s)<n-k.\\]"}</SafeMJ>
      Entonces, fíjate que:
      <SafeMJ scrollable>{"\\[c(x):=m(x)x^{n-k}-s(x),\\tag{6.3}\\]"}</SafeMJ>
      representa una palabra de <SafeMJ after=",">{"\\(\\mathcal{C}_g\\)"}</SafeMJ> pues tiene grado menor que <SafeMJ>{"\\(n\\)"}</SafeMJ> y es múltiplo del polinomio generador.
      Pero es que, además, como <SafeMJ after=",">{"\\(\\mathrm{gr}(s)<n-k\\)"}</SafeMJ> al restar <SafeMJ>{"\\(s(x)\\)"}</SafeMJ> a <SafeMJ>{"\\(m(x)x^{n-k}\\)"}</SafeMJ> no
      se modificarán los coeficientes de grado <SafeMJ>{"\\(n-k\\)"}</SafeMJ> o superior, de forma que la palabra del código contendrá literalmente
      los <SafeMJ>{"\\(k\\)"}</SafeMJ> símbolos del mensaje y añadirá <SafeMJ>{"\\(n-k\\)"}</SafeMJ> símbolos de redundancia:
      <SafeMJ scrollable>{"\\[c=(-s_0,\\ldots,-s_{n-k-1},m_0,\\ldots,m_{k-1}).\\]"}</SafeMJ>
      A este tipo de codificación en la que los símbolos originales del mensaje aparecen directamente en la codificación de la palabra,
      mientras que los restantes contienen únicamente la información redundante necesaria para la corrección de errores, se la denomina,
      por cierto, <b>sistemática</b>.
    </p>

    <p className="mb-3">
      De hecho, de esta propiedad se deduce trivialmente que la nueva codificación que acabamos de definir es inyectiva, pues si dos mensajes difieren
      en alguna componente, entonces sus sendas palabras del código (que incluyen dichas componentes) serán necesariamente distintas.
      Esto implica, a su vez, que no hemos perdido ninguna de las <SafeMJ>{"\\(q^k\\)"}</SafeMJ> palabras de <SafeMJ>{"\\(\\mathcal{C}_g\\)"}</SafeMJ> y
      que seguimos, por tanto, generando exactamente el mismo código que generábamos antes.
    </p>

    <p className="mb-3">
      Respecto a la practicidad de esta nueva versión de Reed-Solomon, creo que a estas alturas ya es bastante evidente por qué se prefiere usar el polinomio generador en lugar de la evaluación directa.
      Lo primero es que para el receptor resulta muy cómodo que el mensaje original aparezca en la palabra del código, pues esto evita tener que hacer transformaciones
      adicionales, como la interpolación de Lagrange que hacíamos con la definición original, para recuperar el mensaje.
    </p>

    <p className="mb-3">
      Segundo. Esta nueva codificación se reduce, básicamente, a calcular: 
      <SafeMJ scrollable>{"\\[s(x)=m(x)x^{n-k}\\bmod g(x)\\tag{6.4},\\]"}</SafeMJ>
      que no es más que tomar el resto de una división de un polinomio de grado menor que <SafeMJ>{"\\(n\\)"}</SafeMJ> entre otro de grado <SafeMJ after=",">{"\\(n-k\\)"}</SafeMJ> lo cual
      conlleva <SafeMJ>{"\\(O(k(n-k))\\)"}</SafeMJ> operaciones, como ya explicamos en la sección anterior. En la evaluación, en cambio, teníamos que hacer <SafeMJ>{"\\(k\\)"}</SafeMJ> multiplicaciones
      y <SafeMJ>{"\\(k\\)"}</SafeMJ> sumas para cada uno de los <SafeMJ>{"\\(n\\)"}</SafeMJ> puntos de evaluación, luego el total de operaciones era algo mayor: <SafeMJ after=".">{"\\(O(kn)\\)"}</SafeMJ>{" "}
      Pero mejor que eso es el hecho de que existen circuitos súper fáciles de implementar en <em>hardware</em> que aceleran
      enormemente el cálculo de <SafeMJ after=".">{"\\(s(x)\\)"}</SafeMJ> Un clásico son, por ejemplo, los LFSR (<em>Linear Feedback Shift Register</em>), formados por una cadena de registros en la que el
      estado de cada ciclo se utiliza para calcular el estado del siguiente. En particular, los registros almacenan los coeficientes del resto parcial de la división y estos se van actualizando con cada
      símbolo de entrada de <SafeMJ after=".">{"\\(m(x)x^{n-k}\\)"}</SafeMJ> Las conexiones y las operaciones intermedias de la retroalimentación vienen marcadas, como ya estarás imaginando, por <SafeMJ after=",">{"\\(g(x)\\)"}</SafeMJ> que
      es fijo.
    </p>

    <p className="mb-3">
      Y ya por último, y seguramente más importante, este nuevo enfoque de Reed-Solomon resulta especialmente útil para la fase de decodificación.
      Para verlo, supongamos que recibimos un polinomio <SafeMJ after=",">{"\\(r(x)=c(x)+e(x)\\)"}</SafeMJ> donde <SafeMJ>{"\\(c(x)\\)"}</SafeMJ> es el polinomio
      correspondiente a una palabra de <SafeMJ>{"\\(\\mathcal{C}_g\\)"}</SafeMJ> y <SafeMJ>{"\\(e(x)\\)"}</SafeMJ> representa el error introducido 
      durante la transmisión. Entonces, evaluando el polinomio recibido en las raíces del polinomio generador y teniendo en cuenta que <SafeMJ>{"\\(g(x)\\)"}</SafeMJ> divide a <SafeMJ after=",">{"\\(c(x)\\)"}</SafeMJ>{" "}
      obtenemos los llamados <b>síndromes</b>:
      <SafeMJ scrollable>{"\\[S_i:=r(\\alpha^i)=\\underbrace{c(\\alpha^i)}_{\\displaystyle 0}+e(\\alpha^i)=e(\\alpha^i),\\quad \\forall i=1,\\ldots,n-k.\\]"}</SafeMJ>
      Lo potente, como ves, es que los síndromes dependen únicamente del error introducido durante la transmisión, de forma que si todos ellos son cero,
      se puede afirmar que la palabra recibida es válida. Precisamente, esta propiedad es la que explotan muchos de los algoritmos de decodificación
      de Reed-Solomon empleados hoy en día, los cuales consiguen, de hecho, reducir la complejidad computacional de recuperar el mensaje a ¡<SafeMJ after="!">{"\\(O(n^2)\\)"}</SafeMJ> Por mencionar algunos,
      está <b>Berlekamp-Massey</b>, que va construyendo el polinomio localizador de errores iterativamente, o también el <b>algoritmo de Sugiyama</b>, basado en
      el algoritmo de Euclides extendido. Como siempre, te recomiendo que investigues un poco más sobre ellos si te interesa...
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">7. Los códigos QR</h2>

    <p className="mb-3">
      Tal y como anunciamos en la introducción, un caso práctico muy conocido de Reed-Solomon son los códigos QR. Definitivamente esos
      cuadraditos negros y blancos ya están por todas partes y nos los cuelan hasta para la carta de los restaurantes.
      Al menos, gracias a toda la teoría que hemos desarrollado, entender su capacidad correctora va a ser pan comido.
      Todas las especificaciones vienen, de hecho, perfectamente detalladas en el estándar ISO/IEC 18004.
    </p>

    <p className="mb-3">
      Lo primero. Los códigos QR usan Reed-Solomon sobre <SafeMJ after=",">{"\\(\\mathbb{F}_{256}\\cong\\mathbb{F}_2[z]/\\langle z^8+z^4+z^3+z^2+1\\rangle\\)"}</SafeMJ> es decir, el cuerpo de 256 elementos.
      Te dejo como ejercicio demostrar que el polinomio <SafeMJ>{"\\(f(z)=z^8+z^4+z^3+z^2+1\\)"}</SafeMJ> es irreducible en <SafeMJ after=",">{"\\(\\mathbb{F}_2\\)"}</SafeMJ> lo cual es imprescindible,
      ya sabes, para poder aplicar la proposición <SafeMJ after=".">{"\\((1.2)\\)"}</SafeMJ> Es un poco tedioso, pero al final es simplemente comprobar que <SafeMJ>{"\\(f(z)\\)"}</SafeMJ> no es divisible por ningún
      factor irreducible de grado menor o igual a 4, los cuales no hay tantos.
    </p>

    <p className="mb-3">
      Elegir este cuerpo y no otro es, en realidad, bastante clave para nuestro caso de uso. Recuerda de la primera sección del artículo que cada elemento de este cuerpo podía ser representado por un polinomio, en este caso, de grado menor que 8 y con coeficientes en <SafeMJ after=".">{"\\(\\mathbb{F}_2\\)"}</SafeMJ>{" "}
      O lo que es lo mismo, mediante una secuencia de 8 bits, es decir... un <em>byte</em>. Aaaaamigo.
    </p>

    <p className="mb-3">
      De hecho, para simplificar aún más la escritura es común usar la notación decimal en lugar de la binaria. Por ejemplo, <SafeMJ after=".">{"\\(p(z)=z^4+z^3+1\\equiv 11001_2=25\\)"}</SafeMJ>{" "}
      Pero ojo, no olvidemos nunca que no estamos trabajando con enteros habituales. Es decir, <SafeMJ>{"\\(p(z)^2\\)"}</SafeMJ> no es, ni de broma, <SafeMJ after=",">{"\\(25^2\\bmod 256=113\\)"}</SafeMJ>{" "}
      sino que hay que desarrollar y reducir módulo <SafeMJ after=",">{"\\(f(z)\\)"}</SafeMJ> recuerda:
      <SafeMJ scrollable>{"\\[25^2\\equiv(z^4+z^3+1)^2=z^8+z^6+1\\equiv z^6+z^4+z^3+z^2\\equiv 1011100_2\\equiv 92.\\]"}</SafeMJ>
    </p>

    <p className="mb-3">
      Por otra parte, para transformar la cadena de caracteres a codificar en un conjunto de <em>bytes</em>, el estándar permite varias alternativas, dependiendo de los símbolos que queramos incluir.
      Por ejemplo, el <em><b>byte mode</b></em>, uno de los más utilizados, transforma cada carácter de una cadena de texto típica (alfabeto español, diacríticos y algunas letras especiales)
      en un <em>byte</em> siguiendo la norma ISO-8859-1, que no es más que una tabla de conversión arbitraria.
      Por ejemplo, con este modo la URL:
      <SafeMJ scrollable>{"\\[\\texttt{https://jaimegimillo.me}\\]"}</SafeMJ>
      se transforma en la siguiente secuencia de <em>bytes</em>:

    <div className="flex flex-col md:flex-row gap-4 m-5">
      <table className="table-auto border-collapse border border-gray-300 dark:border-gray-700 w-full text-md">
        <thead className="bg-gray-100 dark:bg-gray-800">
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              #
            </th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              Carácter
            </th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              Decimal
            </th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              Binario
            </th>
          </tr>
        </thead>

        <tbody className="bg-white dark:bg-gray-900">
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">1</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{h}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(104\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101000\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">2</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{t}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(116\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01110100\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">3</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{t}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(116\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01110100\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">4</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{p}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(112\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01110000\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">5</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{s}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(115\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01110011\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">6</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{:}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(58\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(00111010\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">7</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{/}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(47\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(00101111\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">8</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{/}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(47\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(00101111\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">9</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{j}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(106\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101010\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">10</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{a}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(97\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01100001\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">11</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{i}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(105\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101001\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">12</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{m}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(109\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101101\\)"}</SafeMJ></td>
          </tr>
        </tbody>
      </table>

      <table className="table-auto border-collapse border border-gray-300 dark:border-gray-700 w-full text-md">
        <thead className="bg-gray-100 dark:bg-gray-800">
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              #
            </th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              Carácter
            </th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              Decimal
            </th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">
              Binario
            </th>
          </tr>
        </thead>

        <tbody className="bg-white dark:bg-gray-900">
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">13</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{e}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(101\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01100101\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">14</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{g}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(103\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01100111\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">15</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{i}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(105\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101001\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">16</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{m}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(109\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101101\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">17</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{i}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(105\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101001\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">18</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{l}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(108\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101100\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">19</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{l}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(108\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101100\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">20</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{o}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(111\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101111\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">21</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{.}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(46\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(00101110\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">22</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{m}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(109\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01101101\\)"}</SafeMJ></td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left">23</td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(\\texttt{e}\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(101\\)"}</SafeMJ></td>
            <td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-left"><SafeMJ>{"\\(01100101\\)"}</SafeMJ></td>
          </tr>
        </tbody>
      </table>
    </div>
      Existen, evidentemente, otros modos de conversión, como el numérico, para codificar únicamente dígitos, o incluso uno para los <em>kanji</em> (los caracteres japoneses).
    </p>

    <p className="mb-3">
      Una vez tenemos la secuencia de <SafeMJ>{"\\(k\\)"}</SafeMJ> <em>bytes</em> que queremos codificar en el QR, toca añadir la redundancia.
      Para ello, el estándar indica usar la codificación sistemática que vimos en la sección anterior, es decir, la <SafeMJ after=",">{"\\((6.3)\\)"}</SafeMJ> usando como polinomio generador:
      <SafeMJ scrollable>{"\\[g(x)=\\prod_{i=0}^{n-k-1}(x-\\alpha^i),\\]"}</SafeMJ>
      con <SafeMJ>{"\\(\\alpha=2\\)"}</SafeMJ> (<SafeMJ after=",">{"\\(z\\)"}</SafeMJ> visto como polinomio) como elemento primitivo de <SafeMJ after=".">{"\\(\\mathbb{F}_{256}\\)"}</SafeMJ>{" "}
      Ver que este <SafeMJ>{"\\(\\alpha\\)"}</SafeMJ> tiene orden 255 parece extremadamente largo y aburrido, pero no lo es tanto en realidad si usamos el Teorema de Lagrange para grupos,
      el cual nos dice que:
      <SafeMJ scrollable>{"\\[o(\\alpha)\\mid |\\mathbb{F}_{256}^\\times|=255=3\\cdot 5 \\cdot 17.\\]"}</SafeMJ>
      Entonces, basta con comprobar (última cosa que te dejo como ejercicio, perdón), que:
      <SafeMJ scrollable>{`
        \\[
        \\begin{aligned}
        \\alpha^{3\\cdot 5} &= z^{15} = z^5+z^2+z \\neq 1,\\\\[4pt]
        \\alpha^{3\\cdot 17} &= z^{51} = z^3+z \\neq 1,\\\\[4pt]
        \\alpha^{5\\cdot 17} &= z^{85} = z^7+z^6+z^4+z^2+z \\neq 1.
        \\end{aligned}
        \\]
      `}</SafeMJ>
      Te recomiendo, si no quieres morir en el intento, que calcules primero las potencias <SafeMJ after=",">{"\\(z^{8},z^{16},z^{32},z^{64}\\)"}</SafeMJ>{" "}
      aprovechándote de que el término cruzado de la segunda igualdad notable desaparece en <SafeMJ after=".">{"\\(\\mathbb{F}_2\\)"}</SafeMJ>
    </p>

    <p className="mb-3">
      Luego, más cosas que necesitamos establecer. ¿Qué <SafeMJ>{"\\(n\\)"}</SafeMJ> y <SafeMJ>{"\\(k\\)"}</SafeMJ> tomamos? La guía
      define hasta 40 versiones de QRs, cada una con mayor capacidad total de almacenamiento (<SafeMJ after=")">{"\\(n\\)"}</SafeMJ> que la anterior.
      Además, para cada una de estas versiones, se contemplan 4 niveles de corrección de errores, los cuales determinan cómo se reparte ese <SafeMJ>{"\\(n\\)"}</SafeMJ> fijo entre <SafeMJ>{"\\(k\\)"}</SafeMJ> y <SafeMJ after=".">{"\\(n-k\\)"}</SafeMJ>{" "}
      En particular, cada nivel fija el porcentaje (bastante) aproximado de <SafeMJ>{"\\(t/n\\)"}</SafeMJ> <em>bytes</em> de la palabra que se pueden recuperar:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"><b>Nivel L</b> (<em>Low</em>): <SafeMJ>{"\\(\\approx 7\\%\\)"}</SafeMJ> de <em>bytes</em> corregibles.</li>
        <li className="mb-1"><b>Nivel M</b> (<em>Medium</em>): <SafeMJ after=".">{"\\(\\approx 15\\%\\)"}</SafeMJ></li>
        <li className="mb-1"><b>Nivel Q</b> (<em>Quartile</em>): <SafeMJ after=".">{"\\(\\approx 25\\%\\)"}</SafeMJ></li>
        <li className="mb-1"><b>Nivel H</b> (<em>High</em>): <SafeMJ after=".">{"\\(\\approx 30\\%\\)"}</SafeMJ></li>
      </ul>
      Aquí se muestra, por ejemplo, dicha distribución para las 3 primeras versiones de QRs:
    </p>

    <div className="flex flex-col md:flex-row justify-center gap-6 m-5 overflow-x-auto">
      {/* V1 */}
      <table className="table-auto border-collapse border border-gray-300 dark:border-gray-700 min-w-[220px] text-md">
        <thead>
          <tr>
            <th
              colSpan={4}
              className="border border-gray-300 dark:border-gray-700 px-3 py-2 text-center bg-gray-100 dark:bg-gray-800"
            >
              V1
            </th>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-left">
              Nivel
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(n\\)"}</SafeMJ>
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(k\\)"}</SafeMJ>
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(n-k\\)"}</SafeMJ>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">L</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">26</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">19</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">7</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">M</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">26</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">16</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">10</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">Q</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">26</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">13</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">13</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">H</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">26</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">9</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">17</td>
          </tr>
        </tbody>
      </table>

      {/* V2 */}
      <table className="table-auto border-collapse border border-gray-300 dark:border-gray-700 min-w-[220px] text-md">
        <thead>
          <tr>
            <th
              colSpan={4}
              className="border border-gray-300 dark:border-gray-700 px-3 py-2 text-center bg-gray-100 dark:bg-gray-800"
            >
              V2
            </th>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-left">
              Nivel
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(n\\)"}</SafeMJ>
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(k\\)"}</SafeMJ>
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(n-k\\)"}</SafeMJ>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">L</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">44</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">34</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">10</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">M</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">44</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">28</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">16</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">Q</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">44</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">22</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">22</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">H</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">44</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">16</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">28</td>
          </tr>
        </tbody>
      </table>

      {/* V3 */}
      <table className="table-auto border-collapse border border-gray-300 dark:border-gray-700 min-w-[220px] text-md">
        <thead>
          <tr>
            <th
              colSpan={4}
              className="border border-gray-300 dark:border-gray-700 px-3 py-2 text-center bg-gray-100 dark:bg-gray-800"
            >
              V3
            </th>
          </tr>
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-left">
              Nivel
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(n\\)"}</SafeMJ>
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(k\\)"}</SafeMJ>
            </th>
            <th className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">
              <SafeMJ>{"\\(n-k\\)"}</SafeMJ>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">L</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">70</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">55</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">15</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">M</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">70</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">44</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">26</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">Q</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">70</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">34</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">36</td>
          </tr>
          <tr>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">H</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">70</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">26</td>
            <td className="border border-gray-300 dark:border-gray-700 px-3 py-1 text-center">44</td>
          </tr>
        </tbody>
      </table>
    </div>

    <p className="mb-3">
      Dependiendo de lo largo que sea nuestro mensaje a codificar y de la redundancia que queramos añadir, elegiremos una versión y nivel u otro.
      Por ejemplo, nuestra URL de antes estaba formada por 23 <em>bytes</em>, luego seguro que tendremos que usar una versión superior a la V1,
      pues en dicha versión <SafeMJ>{"\\(k<23\\)"}</SafeMJ> para todos los niveles.
    </p>

    <p className="mb-3">
      Supongamos, entonces, que escogemos la V2 y el nivel M. ¿Esta vale? Bien, pues para formar el mensaje final a codificar necesitamos añadir primero
      una <b>cabecera</b> (<SafeMJ after="),">{"\\(H\\)"}</SafeMJ> que no es más que una serie de bits iniciales que indican la cantidad de <em>bytes</em> que ocupa el mensaje y el modo (<em>byte</em>, numérico, <em>kanji</em>) empleado
      para obtener dichos <em>bytes</em> a partir de los símbolos originales. Esta información es, como te estarás imaginando, imprescindible para que el lector QR
      pueda recuperar el texto que se transformó a <em>bytes</em>.
      
      Por ejemplo, para nuestra URL y la versión 2 serían los siguientes 12 bits:
      <SafeMJ scrollable>{"\\[H=\\underbrace{0100}_{\\displaystyle\\textnormal{\\textit{byte mode}}}\\underbrace{00010111}_{\\displaystyle 23}.\\]"}</SafeMJ>
      Finalmente, el mensaje se rellena con un <b>terminador</b> (<SafeMJ after=")">{"\\(T\\)"}</SafeMJ> de <SafeMJ after="'s">{"\\(0\\)"}</SafeMJ> (en nuestro caso, 4) para
      que la secuencia total siga teniendo una cantidad de bits múltiplo de 8 y por tanto pueda reagruparse en <em>bytes</em>.
      Mira nuestro ejemplo:
      <SafeMJ scrollable>{`
        \\[
        \\underbrace{0100\\,0001\\mid0111}_{\\displaystyle H}\\,\\underbrace{0110\\mid1000}_{\\displaystyle\\texttt{h}}\\,\\underbrace{0111\\mid0100}_{\\displaystyle \\texttt{t}}\\,\\underbrace{0111\\mid0100}_{\\displaystyle \\texttt{t}}\\ldots\\underbrace{0110\\mid1101}_{\\displaystyle \\texttt{e}}\\,\\underbrace{0110\\mid0101}_{\\displaystyle \\texttt{s}}\\,\\underbrace{0000\\vphantom{\\mid}}_{\\displaystyle T},
        \\]
      `}</SafeMJ>
      o si lo prefieres en decimal:
      <SafeMJ scrollable>{`
        \\[
        65\\mid 118\\mid 135\\mid 71\\mid\\ldots\\mid 214\\mid 80.
        \\]
      `}</SafeMJ>
    En total ves que obtenemos 25 <em>bytes</em>, luego efectivamente la configuración V2-M nos valía, sobrándonos <SafeMJ>{"\\(k-25=3\\)"}</SafeMJ> <em>bytes</em>.
    Estos <em>bytes</em> "extra", de hecho, también deben ser rellenados para alcanzar los <SafeMJ>{"\\(k=28\\)"}</SafeMJ> <em>bytes</em> que marca el nivel. En particular, el estándar manda ir
    completando y alternando siempre con los siguientes <em>bytes</em> de <b><em>padding</em></b>:
    <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
      <li className="mb-1"><SafeMJ>{"\\(11101100_2=236,\\)"}</SafeMJ></li>
      <li className="mb-1"><SafeMJ>{"\\(00010001_2=17.\\)"}</SafeMJ></li>
    </ul>
    Así, el mensaje final de nuestra URL nos queda:
    <SafeMJ scrollable>{`
      \\[
      65\\mid 118\\mid 135\\mid 71\\mid\\ldots\\mid 214\\mid 80\\mid 236\\mid 17\\mid 236,
      \\]
    `}</SafeMJ>
    que en forma de polinomio es el siguiente bicho:
    <SafeMJ scrollable>{"\\[m(x)=65x^{27}+118x^{26}+135x^{25}+71x^{24}+\\ldots+214x^{4}+80x^{3}+236x^{2}+17x+236,\\]"}</SafeMJ>
    Los coeficientes, insisto, no son enteros al uso, sino representaciones de elementos de <SafeMJ after=".">{"\\(\\mathbb{F}_{256}\\)"}</SafeMJ>{" "}
    Que no se diga que no lo he dicho, eh... Observa además el orden de los <em>bytes</em> respecto a dichos coeficientes: <SafeMJ>{"\\(m_i\\)"}</SafeMJ> no es
    en realidad el <SafeMJ after="ésimo">{"\\(i\\text{-}\\)"}</SafeMJ> <em>byte</em>, como sería lo natural, sino el de la posición <SafeMJ after=".">{"\\(k-1-i\\)"}</SafeMJ>{" "}
    Esta "inversión" permite que, si la codificación se implementa mediante un LFSR (recuerda la sección anterior), los símbolos de entrada
    se puedan ir procesando en el mismo orden en que se transmiten, sin necesidad de almacenar el mensaje completo de antemano.
    Un "streaming" de toda la vida, vamos. Tiene más sentido, obviamente, en lecturas de CDs o sistemas de comunicaciones que en el caso de un QR,
    pero bueno, igualmente la guía mantiene esta práctica habitual...
    </p>

    <p className="mb-3">
      Ahora sí, calculemos la redundancia exacta aplicando la fórmula <SafeMJ after=".">{"\\((6.4)\\)"}</SafeMJ> Entenderás que hacer los cálculos a mano para nuestro caso es sencillamente inhumano,
      así que he tenido que tirar de un <a href="/qr_reed_solomon.py" download className="underline">script</a> de Python. Descárgalo si quieres y
      le echas un vistazo. Tiene algún truquito interesante... Sea como sea, el resultado son los siguientes 16 <em>bytes</em> de redundancia:
      <SafeMJ scrollable>{`
        \\[
        203\\mid 190\\mid 51\\mid 143\\mid 221\\mid 26\\mid 162\\mid 71\\mid 122\\mid 177\\mid 35\\mid 100\\mid 214\\mid 116\\mid 191\\mid 221.
        \\]
      `}</SafeMJ>
      Añadiéndolos a continuación de los <SafeMJ>{"\\(28\\)"}</SafeMJ> <em>bytes</em> que ya teníamos, completamos por fin
      la palabra del código (invertida) de longitud <SafeMJ after=",">{"\\(n=44\\)"}</SafeMJ> lista para volcarse en la matriz del QR.
      Pero ¿cómo hacemos esto último? Es decir, ¿cómo formamos el conjunto final de cuadraditos (<b>módulos</b>) negros y blancos?
    </p>

    <p className="mb-3">
      Bien, pues lo primero que hay que tener claro es dónde colocar, luego ya veremos cómo. No debería sorprendernos que un QR completo
      contenga más cosas aparte del mensaje codificado y la redundancia. Dependiendo de la versión específica, existirán unos módulos fijos (<b>patrones</b>)
      u otros que ayudarán al lector QR a escanear el mensaje. Algunos de los más típicos son:
      <ul className="list-disc list-inside sm:list-outside sm:pl-5 sm:ml-5 my-3 text-left">
        <li className="mb-1"><b>Patrón buscador</b>: los míticos 3 cuadrados grandes de las esquinas, los cuales sirven para localizar y orientar el QR dentro de la imagen.</li>
        <li className="mb-1"><b>Patrón de alineación</b>: punto extra de referencia para corregir la distorsión de perspectiva.</li>
        <li className="mb-1"><b>Patrón de temporización</b>: línea discontinua entre los buscadores que permite conocer el tamaño exacto de un módulo.</li>
        <li className="mb-1"><b>Información de formato</b>: toda la información adicional de configuración que necesita conocer el lector QR, como por ejemplo, el nivel de corrección de errores que se usó.</li>
      </ul>
      Aquí tienes, por ejemplo, dónde se ubican dichos patrones en la V2:
    </p>

    <img src={qr_anatomy} alt="Zonas de la matriz QR versión 2" className="mx-auto my-4 max-w-[40rem] w-full" />

    <p className="mb-3">
      En versiones más grandes la cosa se complica un poco más... Ya aparecen varios patrones de alineación repartidos por toda la matriz
      (¡hasta 46 en la versión 40!), y a partir de la versión 7, se añade un patrón con información sobre la versión del QR, el cual empieza
      a venir bien al ser cada vez más pequeña la diferencia relativa de tamaño entre una versión y la superior. 
    </p>

    <p className="mb-3">
      Entonces, una vez tenemos definida y ubicada nuestra zona gris de colocación, podemos empezar a rellenarla. Para ello, la idea es que cada bit de
      la palabra (invertida) representa un cuadradito: 1 = negro y 0 = blanco, y los vamos colocando de uno en uno en forma de <b>zigzag</b>. En concreto, se
      empieza por la esquina inferior derecha de la matriz y se va avanzando en parejas de columnas, subiendo hasta arriba del todo. Al llegar arriba,
      se pasa a la pareja de columnas inmediatamente a la izquierda y se baja de nuevo hasta abajo. Y si por el camino nos topamos con un módulo fijo, es
      decir, uno ocupado por algún patrón, sencillamente omitimos esa posición y pasamos a la siguiente. Y así sucesivamente hasta colocarlos todos... Un poco de lío, ¿no?
      Mejor, si no, mira el caso concreto de la versión 2, con el que se ve perfectamente la serpientecilla que queda:
    </p>

    <img src={qr_zigzag} alt="Recorrido en zigzag de colocación de bits en una matriz QR versión 2" className="mx-auto my-4 max-w-xl w-full" />

    <p className="mb-3">
      Además, si el mensaje es tan largo que no puede ser codificado con un único Reed-Solomon (recuerda la restricción <SafeMJ after="),">{"\\(n\\le q-1=255\\)"}</SafeMJ>{" "}
      entonces los datos se trocean en varios bloques independientes, cada uno con su propio Reed-Solomon aplicado por separado.
      Luego, a la hora de colocarlos en la matriz, lo que se hace es entrelazar sus <em>bytes</em>, es decir, se escribe, por ejemplo: <em>byte</em> 1 del primer bloque, <em>byte</em> 1
      del segundo bloque, <em>byte</em> 2 del primer bloque, <em>byte</em> 2 del segundo bloque... como repartiendo cartas.
      Esta intercalación es súper útil, pues si, por ejemplo, una mancha de café se carga una zona concreta de la matriz, el daño se reparte entre
      varios bloques de Reed-Solomon en vez de concentrarse todo en uno solo.
    </p>

    <p className="mb-3">
      Genial, pues solo falta una cosa más. La última, de verdad. Resulta que te he contado una media verdad, pues el bit que ves
      en cada módulo del QR no es exactamente el bit "en crudo" original. Para obtener el color final que
      ves pintado, antes hay que pasarlo por un último filtro: la <b>máscara</b>, la cual aplica a cada módulo no fijo de la matriz
      la siguiente operación XOR:
      <SafeMJ scrollable>{"\\[\\text{color final} = \\text{bit} \\oplus \\text{máscara}(\\text{fila},\\text{columna}).\\]"}</SafeMJ>
    </p>

    <p className="mb-3">
      En total existen 8 posibles máscaras, numeradas del 0 al 7, cada una definida, como ves, por una fórmula sobre la fila y la columna que ocupa
      cada bit. Por ejemplo, la máscara 0 invierte un módulo si la suma de su fila y columna es par. Es decir:
      <SafeMJ scrollable>{"\\[\\text{máscara}_0 (i,j)=(i+j+1)\\bmod 2.\\]"}</SafeMJ>
      Hay también fórmulas más complicadas, claro. Mira esta para la máscara 6:
      <SafeMJ scrollable>{"\\[\\text{máscara}_6 (i,j)=\\left((i\\cdot j)\\bmod 2+(i\\cdot j)\\bmod 3+1\\right)\\bmod 2.\\]"}</SafeMJ>
      Okey, pero ¿esto para qué? Pues la idea es evitar que ciertos mensajes generen patrones visuales problemáticos, como
      zonas enteras de módulos idénticos, o formas que se parezcan a patrones fijos y puedan confundir al lector QR.
      Digamos que la máscara "revuelve" estadísticamente los datos para que eso no ocurra. De hecho, para elegir qué máscara usar, el codificador
      prueba con todas ellas y finalmente decide en base a una serie de criterios de estilo, tipo: número de bloques 2x2 del mismo color, filas/columnas
      con muchos módulos iguales seguidos, etc.
    </p>

    <p className="mb-3">
      En nuestro caso particular de la URL y la versión V2-M, resulta que la máscara ganadora es la 6. Veamos, pues, cómo aplica para nuestro primer <em>byte</em> a
      colocar, el <SafeMJ after=":">{"\\(65=01000001_2\\)"}</SafeMJ>
    </p>

    <div className="overflow-x-auto my-5">
      <table className="table-auto sm:table-fixed border-collapse border border-gray-300 dark:border-gray-700 w-full text-md mx-auto">
        <thead className="bg-gray-100 dark:bg-gray-800">
          <tr>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">#</th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">Fila</th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">Columna</th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">Bit</th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">Máscara</th>
            <th className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">Color final</th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900">
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">0</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">24</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">24</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="oscuro" title="oscuro" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">1</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">24</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">23</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="claro" title="claro" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-white" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">2</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">23</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">24</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="oscuro" title="oscuro" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">3</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">23</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">23</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="oscuro" title="oscuro" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">4</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">22</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">24</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="oscuro" title="oscuro" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">5</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">22</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">23</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="oscuro" title="oscuro" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">6</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">21</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">24</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="oscuro" title="oscuro" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
          <tr><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">7</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">21</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center">23</td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(1\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><SafeMJ>{"\\(0\\)"}</SafeMJ></td><td className="border border-gray-300 dark:border-gray-700 p-1 sm:px-3 text-center"><span role="img" aria-label="oscuro" title="oscuro" className="inline-block w-4 h-4 align-middle border border-gray-400 bg-black" /></td></tr>
        </tbody>
      </table>
    </div>

    <p className="mb-3">
      Simplemente hay que repetir este proceso con todos los <em>bytes</em>, colocarlos según el zigzag que vimos, añadir la información de los patrones
      y... ¡listo! Ya tenemos nuestro QR montado. Prueba a escanearlo abajo, ya verás como sale la URL de esta web.
    </p>

    <p className="mb-3">
      De hecho, he aprovechado para comprobar que la teoría que hemos desarrollado en este artículo funciona,
      modificando a posta 8 de los <em>bytes</em> del mensaje. En particular, "manchando" (marcando de negro) los de las posiciones 15, 16, 21, 22, 27, 28, 33 y 34.
      Como en nuestro caso <SafeMJ after=",">{"\\(t=\\lfloor\\tfrac{n-k}{2}\\rfloor = 8\\)"}</SafeMJ> el lector es perfectamente capaz de recuperar el texto.
      Pero si manchamos un solo <em>byte</em> más (el 32), ya nos pasamos del límite y el lector efectivamente es incapaz de escanear... ¡Brutal!
    </p>

    <div className="flex flex-wrap justify-center items-start gap-10 sm:gap-12 my-6">
      {renderQR(
        QR_ORIG,
        <>QR original (V2-M) de <b>https://jaimegimillo.me</b></>,
        "QR original (V2-M) de https://jaimegimillo.me"
      )}
      {renderQR(
        QR_DMG,
        <>El mismo QR con 8 <em>bytes</em> alterados. ¡Pruébalo, sigue funcionando!</>,
        "El mismo QR con 8 bytes alterados. ¡Pruébalo, sigue funcionando!",
        QR_DIFF
      )}
      {renderQR(
        QR_DMG_9,
        <>Con 9 <em>bytes</em> alterados, uno más <br/> que <SafeMJ after=",">{"\\(t\\)"}</SafeMJ> ya no se puede leer.</>,
        "Con 9 bytes alterados, uno más que t, ya no se puede leer.",
        QR_DIFF_9
      )}
    </div>
    </>
  );
}
